/**
 * AgentHub product roadmap — single source of truth.
 *
 * Manually curated from the partner's master spec
 * (https://docs.google.com/document/d/1ebkIHRPZlZBVjXkHsWw4cy8SsgDw8XUn5GZ9h45c07k)
 * + my memory of what's already shipped on multi-ai. Keep this list
 * in sync with reality: when something lands on `main`, flip status
 * to "done" and add a `doneIn` reference.
 *
 * UI: rendered by /app/roadmap/page.tsx with filters + 80/20 highlights.
 * AI: see /root/.claude/projects/-root/memory/agenthub_roadmap.md for
 *     the canonical pointer to this file.
 */

export type RoadmapArea =
  | "platform"
  | "director"
  | "customer_journey"
  | "site"
  | "sales"
  | "analytics"
  | "buyer"
  | "hr"
  | "recruiter"
  | "content"
  | "approvals"
  | "memory"
  | "integrations"
  | "schedules"

export type RoadmapStatus = "done" | "partial" | "planned" | "backlog"

/**
 * Effort sizing — calibrated to a focused day:
 *   S  (Small)   — 2-4 hours
 *   M  (Medium)  — 1-2 days
 *   L  (Large)   — 3-7 days
 *   XL (Huge)    — 2+ weeks
 */
export type RoadmapEffort = "S" | "M" | "L" | "XL"

export interface RoadmapItem {
  id: string
  title: string
  area: RoadmapArea
  description: string
  status: RoadmapStatus
  effort: RoadmapEffort
  /** 1-5 — value to a Romashka-class owner (revenue, time saved, decision quality). */
  impact: 1 | 2 | 3 | 4 | 5
  /** Optional pointer to where it landed (commit SHA / phase doc). */
  doneIn?: string
  /** Optional note: current state, blockers, what's missing. */
  note?: string
  /**
   * Reference to the partner's master spec — free-text section name
   * so the reader knows where the requirement came from (e.g.
   * "Розділ 5 · Блок 2", "ВП block · Sales workspace").
   */
  docRef?: string
  /**
   * Detailed description from the spec — what this item is supposed
   * to do, key sub-features, examples. Plain text with `\n\n` for
   * paragraph breaks and `- ` for bullets. The card click-to-expand
   * panel renders this.
   */
  details?: string
  /**
   * Чи входить пункт у MVP-scope (запуск публічної платформи).
   * true → треба до запуску. undefined або false → post-MVP.
   * MVP-progress = (mvp+done) / mvp_total — окремий рядок у hero card.
   */
  mvp?: boolean
}

// ─── Areas display metadata ─────────────────────────────────────────────────

export const AREA_META: Record<
  RoadmapArea,
  { label: string; emoji: string; description: string }
> = {
  platform: {
    label: "Платформа",
    emoji: "🏠",
    description: "Головна, sidebar, картка бізнесу, оболонка кабінету",
  },
  director: {
    label: "Директор",
    emoji: "🎯",
    description: "AI Director — діалог, brain, scoring, weekly review",
  },
  customer_journey: {
    label: "Шлях клієнта",
    emoji: "🛤️",
    description: "Overview + Site + Instagram + Offline воронки",
  },
  site: {
    label: "Сайт-агент",
    emoji: "🌐",
    description: "Воронка сайту, агент проблем, технічна придатність, SEO+AI",
  },
  sales: {
    label: "Продажі",
    emoji: "💰",
    description: "Sales workspace для менеджера, режими A0-A2, KeyCRM",
  },
  analytics: {
    label: "Аналітик",
    emoji: "📊",
    description: "KPI Hub, аномалії, причинно-наслідковий аналіз",
  },
  buyer: {
    label: "Закупівельник",
    emoji: "📦",
    description: "Агент закупки, прогноз попиту, матриця постачальників",
  },
  hr: {
    label: "HR",
    emoji: "👥",
    description: "HR-агент: оцінка команди, ролі, KPI, навчання",
  },
  recruiter: {
    label: "Рекрутер",
    emoji: "🎤",
    description: "AI-рекрутер: вакансії, відео-інтерв'ю, оцінка кандидатів",
  },
  content: {
    label: "Контент",
    emoji: "✍️",
    description: "Content-агент: пости, Reels, тексти карток, banners",
  },
  approvals: {
    label: "Approvals + Telegram",
    emoji: "✅",
    description: "Черга погоджень, Telegram-flow для власника й менеджера",
  },
  memory: {
    label: "Пам'ять і знання",
    emoji: "🧠",
    description: "Brain (rules), business_dna, knowledge base, decision journal",
  },
  integrations: {
    label: "Інтеграції",
    emoji: "🔌",
    description: "GA4, FB Ads, IG, CRMs, банки, Nova Poshta, MCP-сервери",
  },
  schedules: {
    label: "Розклад і скіли",
    emoji: "⏰",
    description: "Cron-скіли, Morning Brief, Weekly Report, моніторинг",
  },
}

// ─── Status display metadata ────────────────────────────────────────────────

export const STATUS_META: Record<
  RoadmapStatus,
  { label: string; emoji: string; tone: "emerald" | "amber" | "sky" | "zinc" }
> = {
  done: { label: "Готово", emoji: "✅", tone: "emerald" },
  partial: { label: "Частково", emoji: "🟡", tone: "amber" },
  planned: { label: "Заплановано", emoji: "📅", tone: "sky" },
  backlog: { label: "Бэклог", emoji: "💭", tone: "zinc" },
}

// ─── Effort weights for 80/20 ranking ───────────────────────────────────────

const EFFORT_WEIGHT: Record<RoadmapEffort, number> = {
  S: 1,
  M: 2,
  L: 4,
  XL: 8,
}

/**
 * 80/20 score = (impact × 2) ÷ effort_weight. Higher = better candidate.
 * Already-done items return 0 (excluded from "next best" surface).
 */
export function eightyTwentyScore(item: RoadmapItem): number {
  if (item.status === "done") return 0
  return Math.round(((item.impact * 2) / EFFORT_WEIGHT[item.effort]) * 100) / 100
}

// ─── The actual roadmap inventory ───────────────────────────────────────────

export const ROADMAP_ITEMS: RoadmapItem[] = [
  // ── Platform ──
  {
    id: "platform-home",
    title: "Головна сторінка платформи",
    area: "platform",
    description: "Стан компанії · висновок директора · CJ snapshot · картки агентів · план 7/30 · аномалії",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "Phase A `/panel` + Phase F `/company-state` (commit 9e02ae0+)",
    note: "Cockpit зі spec-aligned 40/35/15/10 (Результат/Здоров'я/Шлях/Впровадження)",
  },
  {
    id: "platform-business-card",
    title: "Картка бізнесу — 6 вкладок",
    area: "platform",
    description: "Бізнес · Канали · Команда · Правила бренду · Процеси · Що знає система",
    status: "partial",
    effort: "L",
    impact: 4,
    mvp: true,    note: "MVP працює (`/dashboard/business-card` + import). Ще треба: повний редактор 6 вкладок з версійністю",
    docRef: "Картка бізнесу — Розділ 4 + Director 2.0 Розділ 24-25",
    details: `Картка бізнесу — це центральний мозок платформи. Паспорт + база знань + правила + контекст для директора й аналітика + tone of voice + правила ескалації + основа доступів.

Шість вкладок які має заповнити власник через сесії з директором:
- Бізнес — назва, тип, ціль, біль, готовність %
- Канали і системи — сайт, Instagram, CRM, банки, NP
- Команда і ролі — менеджери, оператори, KPI кожного
- Правила бренду — tone of voice, заборонені фрази, ескалації
- Процеси — типові, нестандартні, винятки
- Що вже знає система — все що директор збирав через діалоги

Мінімум для запуску — 4 технічні властивості: версійність, часткове заповнення (можна заповнювати поетапно), рольовий доступ, прив'язка до поведінки системи.`,
  },
  {
    id: "platform-sidebar-nesting",
    title: "Sidebar з ієрархією",
    area: "platform",
    description: "Сайт + Instagram як child of «Шлях клієнта»",
    status: "done",
    effort: "S",
    impact: 2,
    mvp: true,    doneIn: "0969c15 + 9e02ae0",
  },
  {
    id: "platform-implementation-path",
    title: "Шлях впровадження (XP-based)",
    area: "platform",
    description: "Level 1-10 на базі картки бізнесу + 10 stages + список наступних кроків",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "3e5c945 — `/dashboard/implementation-path`",
  },
  {
    id: "platform-public-roadmap",
    title: "Public roadmap page (ця сторінка)",
    area: "platform",
    description: "Roadmap з 80/20 + статусами для розуміння пріоритетів",
    status: "done",
    effort: "S",
    impact: 3,
    mvp: true,    doneIn: "roadmap.it-contrast.com.ua",
  },

  // ── Director ──
  {
    id: "director-m0-registry",
    title: "Director M0 — Registry + prompt diet",
    area: "director",
    description: "Registry SSOT (21 інтеграція + 6 ролей + 41 tool) + prompt caching breakpoints",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "Phase 09-01 — 70% input cost reduction",
  },
  {
    id: "director-m1-memory",
    title: "Director M1 — Filesystem memory",
    area: "director",
    description: "business_dna.md, commitments.md, okrs.md, reflections/* + 6 tools для Director",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "agenthub_m1_complete",
  },
  {
    id: "director-m2-briefs",
    title: "Director M2 — Morning Brief + Weekly Report",
    area: "director",
    description: "Daily 9:00 brief + Weekly Report у понеділок 9:00",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "agenthub_m3_complete",
  },
  {
    id: "director-m4-anomalies",
    title: "Director M4 — Anomaly engine (z-score)",
    area: "director",
    description: "metric_snapshots + 30d baseline + detect_anomalies tool",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "agenthub_m4_complete",
  },
  {
    id: "director-self-aware",
    title: "Self-Aware Director (Eyes/Hands/Critique/Playbook)",
    area: "director",
    description: "read_agent_output, execute_http_request, self_critique, self-playbook",
    status: "done",
    effort: "XL",
    impact: 5,
    mvp: true,    doneIn: "Phase 09-10..09-14",
  },
  {
    id: "director-brain-rules",
    title: "Director Brain — rules + 9 typed prefs",
    area: "director",
    description: "director_rules table + Mirror-Action-Confirm + add_rule/remove_rule",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "Phase 09-09",
  },
  {
    id: "director-phase13-invariants",
    title: "Phase 13 — anti-hallucination invariants",
    area: "director",
    description: "Memory/Numeric/Action/Credentials claim-checker + claim-corrector + activity log",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "Phase 13",
  },
  {
    id: "director-phase15-pes",
    title: "Phase 15 — Plan-Execute-Synthesize pipeline",
    area: "director",
    description: "Intent classifier + structured anti-hallucination для data_query",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "Phase 15",
  },
  {
    id: "director-spin-onboarding",
    title: "SPIN-онбординг (8 питань 7-10 хв)",
    area: "director",
    description: "Біль · Ціль · Канали · Навантаження · Системи · Хаос · Режим · Перемога",
    status: "partial",
    effort: "M",
    impact: 4,
    mvp: true,    note: "Базовий onboarding є, треба адаптувати під SPIN-логіку зі spec-доку",
    docRef: "Director 2.0 — Розділ 10 (Перша мікросесія)",
    details: `Перша мікросесія директора — 7-10 хв. Не аудит, а 8 структурованих питань щоб зрозуміти бізнес перед створенням плану:

1. Активна біль — "Що зараз найбільше заважає вам заробляти більше?" → визначає кут входу (продажі / сайт / закупка / HR)
2. Ціль на 30 днів — "Якщо через 30 днів покращиться лише одне, що це має бути?"
3. Канали продажу — сайт / IG / TG / TikTok / FB / офлайн / маркетплейси
4. Операційне навантаження — "Хто тягне рутину: ти, керівники чи менеджери?"
5. Наявність систем — сайт + CRM є? → AI Orchestration Layer (підключаємо зверху) vs AI CRM Lite
6. Найхаотичніший відділ — продажі / сайт / закупка / команда / контент → перший вузол втручання
7. Режим роботи — швидко гасити пожежу / паралельно / послідовно
8. Критерій першої перемоги — "За якою ознакою через 7 днів зрозумієте що ми корисні?"

Після цього система формує: стартову оцінку компанії, головний висновок, рекомендованого першого агента, план на 7 днів, картку бізнесу зі стартовими даними.`,
  },
  {
    id: "director-decision-journal",
    title: "Decision journal (журнал рішень)",
    area: "director",
    description: "Хронологія approval → дія → ефект з посиланням на метрики",
    status: "planned",
    effort: "M",
    impact: 4,
    note: "Activity log є — потрібен окремий screen з filter by action_type + linked metrics",
    docRef: "Director 2.0 — Розділ 30 (DecisionLog) + Сайт-агент 29",
    details: `Журнал рішень — окрема сутність DecisionLog яка фіксує всі управлінські рішення:
- хто запропонував (агент / директор / власник)
- що зроблено (approve / reject / postpone)
- очікуваний ефект та фактичний (через 7 днів)
- посилання на метрики які змінились

Потік: агент знаходить проблему → аналітик підтверджує вплив на цифри → директор ставить пріоритет → система задач створює задачу → команда впроваджує → агент перевіряє ефект → журнал рішень фіксує результат.

Це не лог дій — це джерело довіри. Власник може через місяць подивитись "що ми насправді зробили і що це дало". Зараз activity_log містить технічні події, а DecisionLog — це управлінський рівень.`,
  },

  // ── Customer Journey ──
  {
    id: "cj-overview",
    title: "CJ Overview — 6 metric cards + 3 channel cards",
    area: "customer_journey",
    description: "Обсяг · Замовлень · Товарів · AOV · Прибуток · Маржа + Site/IG/Offline + 30d sparklines",
    status: "done",
    effort: "M",
    impact: 5,
    mvp: true,    doneIn: "3c98305 + 0d1aa72 (per IMG_7887)",
  },
  {
    id: "cj-site-funnel",
    title: "CJ Site — 11-stage funnel",
    area: "customer_journey",
    description: "Воронка від відвідувача до повторної покупки + drilldown по стадіях",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "Phase 06 site-funnel",
  },
  {
    id: "cj-instagram",
    title: "CJ Instagram detail",
    area: "customer_journey",
    description: "11-stage воронка IG (review IMG_7887 styling)",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "d8d31a8",
  },
  {
    id: "cj-funnel-comparison",
    title: "CJ Воронка — порівняння періодів",
    area: "customer_journey",
    description:
      "Нова сторінка/вкладка: референс (середній день) vs порівнюваний період, накопичено до тієї ж години. Сайт (GA4 погодинно) + Instagram (локальні таблиці), лише КІЛЬКІСНІ показники (якісний вигляд прибрано 07-21), шкала відхилення, Висновки/Сигналізація/Тестування, Clarity.",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,
    doneIn: "quick-260610-jcf",
    docRef: "Google Doc «Шлях клієнта для Romashka» (партнерське ТЗ, 16 розділів)",
    note: "v1: головна воронка + фільтр-пресети + 3 блоки + Clarity. Deferred: підетапи (drilldown), zoom нижньої воронки, custom date-range UX, авто-сигналізація кожні 30 хв через heartbeat → Telegram, LLM-наратив висновків.",
    details: `Власник за 10-20с бачить: канал працює краще/гірше за норму, де просідання, кількість чи якість проблема, що робити.

Логіка: референс = середній день у вибраному вікні, накопичено 00:00→H; порівнюваний = сьогодні (або інше вікно) до тієї ж H. Чесне порівняння «середній день до 16:00 vs сьогодні до 16:00».

- 2 канали: Сайт (GA4 sessions+events погодинно, additive) / Instagram (dm_messages + order_drafts + dm_thread_reads, thread → година першого inbound)
- тумблер Кількісні (цифри) / Якісні (конверсія %)
- воронка: Етап | Форма (монотонний бар) | Відхилення (шкала центр-0) | Цифри (Реф/Сьогодні/Різниця)
- 3 блоки: Висновки (детермінований наратив) / Сигналізація (поріг 10% / 1 п.п.) / Тестування (→ діалог з Директором)
- Clarity лінк (тільки Сайт)`,
  },
  {
    id: "cj-offline",
    title: "CJ Offline — воронка офлайн-каналу",
    area: "customer_journey",
    description: "Дзвінки → консультації → продажі. Інтеграція Binotel + офлайн-чек",
    status: "planned",
    effort: "L",
    impact: 3,
    note: "Зараз empty placeholder. Binotel webhooks вже підключені — треба склеїти у воронку",
    docRef: "Розділ 11 (Customer Journey: офлайн)",
    details: `9 етапів офлайн-воронки з KPI:
- Трафік біля магазину (foot traffic)
- Вхід у магазин (entry rate)
- Контакт із консультантом (contact rate)
- Примірка (fitting rate)
- Покупка (store conversion)
- Допродаж (upsell rate)
- Лояльність (loyalty capture)
- Повернення / обмін (return rate)
- Повторний візит (repeat visit)

Офлайн не може лишатись "сліпою зоною". Мінімум — продажі по магазинах, середній чек, конверсія (якщо є лічильник), повернення, NPS, повторна покупка через програму лояльності, які товари купують офлайн краще.

Дані: Binotel дзвінки (вже інтегровано) + офлайн-чек з POS / 1С + ручне вводимо foot traffic. Окрема витратна частина: оренда, ЗП консультантів, утиліти.`,
  },
  {
    id: "cj-hypotheses",
    title: "Блок гіпотез у CJ",
    area: "customer_journey",
    description: "Що буде якщо CR на стадії X виросте на Y% — детермінований UAH-калькулятор",
    status: "planned",
    effort: "M",
    impact: 4,
    note: "Партнер у спеці окреслив input-форми + формули. Готова база — funnel + AOV вже є",
    docRef: "Розділ 10 (Блок гіпотез) — у CJ doc",
    details: `Один із ключових блоків продукту. У кожній воронці має бути 2 режими: Факт (реальні дані) і Гіпотеза (що буде, якщо змінити показники).

Два типи гіпотез:
- Гіпотези росту — конверсія, середній чек, допродаж, повторна покупка
- Гіпотези ефективності — маркетинг, ЗП, логістика, оренда, операційні витрати, податки

Власник питає:
- "Якщо я покращу додавання в кошик з 2.42% до 3.2%, що буде з оборотом і прибутком?"
- "Якщо зменшу маркетингові витрати IG з 33% до 25%, що буде з чистим прибутком?"
- "Якщо офлайн зменшить оренду на 15%, чи стане точка прибутковою?"

UI: кнопка "Створити гіпотезу" відкриває копію фактичної воронки де можна змінювати конверсійні і витратні поля. Система автоматично рахує: новий оборот, новий прибуток, ROI зміни, термін окупності.`,
  },
  {
    id: "cj-cod-branch",
    title: "Гілка накладеного платежу",
    area: "customer_journey",
    description: "Окрема воронка від оформлення до отримання + % викупу",
    status: "planned",
    effort: "M",
    impact: 4,
    note: "Nova Poshta TTN tracking є — треба зробити окремий subview",
    docRef: "Розділ 7 (Окрема гілка накладеного платежу)",
    details: `Накладений платіж потребує окремої воронки бо у нього своя економіка втрат:
- Замовлено з накладеним
- Передано у НП
- Доставлено у відділення
- Викуплено клієнтом
- Не викуплено → повернення

KPI: % викупу, середній термін до викупу, повернення %.

Витратна частина: зворотна логістика (50-90 грн × повернення), заморожені кошти у дорозі, упаковка, час менеджера на дзвінки-нагадування.

Власник має побачити: на вході 100 замовлень з накладеним → 73 викуплено → 27 повернень × 80 грн = 2 160 грн збитку зі зворотної логістики, плюс заморожені 27 × AOV.`,
  },

  // ── Site agent ──
  {
    id: "site-agent-card",
    title: "SiteAgentCard — LLM analysis worst stage",
    area: "site",
    description: "Детектить worst stage + LLM problem narrative + UAH recovery (детермінований)",
    status: "done",
    effort: "M",
    impact: 5,
    mvp: true,    doneIn: "6dc8289",
  },
  {
    id: "site-page-audit",
    title: "Аналіз сторінок (КонвершинАП) — посторінковий конверсійний аудит",
    area: "site",
    description: "Вкладка «Аналіз сторінок»: краул сайту → скіл website-audit (Sonnet 5) → score по сторінках, чекліст елементів, проблеми-рішення 🔴/🟡/🟢, план дій, кнопка «Створити задачі». Кеш + оновлення по кнопці",
    status: "done",
    effort: "L",
    impact: 5,
    doneIn: "2026-07-17 (45740c3)",
    details: `Двигун — скіл website-audit (~/.claude/skills), його методологія (6 шарів) і чекліст вбудовані у server-сервіс site-page-audit.ts.

Пайплайн: краул діверсного семпла сторінок (sitemap-index + homepage-лінки, browser-UA, гарантована картка товару, probe /pro-nas на 404) → один виклик Sonnet 5 → структурований JSON. Зведені метрики (критичні/слабкі/ризик/середній score) рахуються в коді (anti-hallucination). Кеш 24h у tenant_meta; авто-обрахунок при першому заході, далі кеш, оновлення кнопкою (force).

UI під макет: score-ring + вердикт + 3 колонки (що знайдено/чому впливає/порада), 5 карток-метрик, фільтри (тип/статус/критичність), список сторінок зі score+статусом+чеклістом, per-page чекліст (елемент/статус/критичність/коментар/рішення), блок «Проблеми та рішення» з чекбоксами → «Створити задачі з вибраних недоліків», план дій за пріоритетом, «Що працює добре».

Живий прогін romashka: 8 сторінок (2 картки товару, інфо, каталог, кошик, 404 «Про нас»), знайшов витік коду шаблону, русизми, суперечності доставки/повернення, битий таймер.

Оновлення 17.07: вкладку «Аналітика» (застарілий GA-огляд) прибрано; на «Головній» додано зведену картку аудиту сторінок (score + метрики + топ проблем, лише кеш через peek-режим — головна не гальмує) з переходом на повний аналіз.

Оновлення 17.07 (2): «Аналіз сторінок» став 4-м джерелом єдиного списку «Пріоритезовані проблеми та покращення» (поряд із Clarity/GA/HTML-аудитом). Його конверсійні issues ранжуються разом з рештою за формулою впив×впевненість/складність; джерело cache-only (peek, без важкого перерахунку з агрегатора); дедуп проти SEO-аудиту (SEO-категорії відкидаються + схожість заголовків). Додано у combined score, картку джерела, pill, кнопку «Відкрити».`,
  },
  {
    id: "site-tech-readiness",
    title: "Технічна придатність + PageSpeed",
    area: "site",
    description: "4 readiness items + Lighthouse 4 gauges + Core Web Vitals",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "84efb06 + f46ed88",
  },
  {
    id: "site-seo-ai-layer1",
    title: "SEO+AI · Шар 1: структурний аудит сайту",
    area: "site",
    description: "Що ми перевіряємо НА БОЦІ сайту: robots.txt дозволи, llms.txt, JSON-LD, server-side рендеринг, мета-теги. 5 рингів + LLM висновок",
    status: "done",
    effort: "M",
    impact: 5,
    mvp: true,    doneIn: "a604486 + f79bb61",
    docRef: "Власна імплементація — /dashboard/site → SEO + AI",
    details: `Це той аудит який зараз працює на /dashboard/site → вкладка SEO + AI. Live на Romashka.

Що перевіряє (без жодного запиту до LLM):
- robots.txt — чи власник ДОЗВОЛИВ 8 AI-ботам індексувати (GPTBot, ClaudeBot, PerplexityBot, Google-Extended, OAI-SearchBot, Bytespider, CCBot, Applebot-Extended)
- /llms.txt + /llms-full.txt — наявність маніфесту
- sitemap.xml — наявність + кількість URL
- JSON-LD — Organization/WebSite на головній, Product/BreadcrumbList на 3 PDP
- Server-side контент — скільки символів видимого тексту в HTML (LLM не виконують JS)
- Meta — title (30-65), description (70-165), canonical, h1, lang, OpenGraph

5 рингів-оцінок (Lighthouse-style 0-100) + загальна + LLM висновок з headline / wins / risks / nextActions. Phase 13 anti-hallucination — числа в коді, LLM лише prose.

ВАЖЛИВО: це НЕ перевіряє чи LLM РЕАЛЬНО знають про сайт — лише чи власник створив технічні умови щоб вони могли. Для перевірки реального знання — див. Шар 2.`,
  },
  {
    id: "site-seo-ai-layer2",
    title: "SEO+AI · Шар 2: live запити в LLM",
    area: "site",
    description: "Що Claude/ChatGPT/Perplexity/Gemini РЕАЛЬНО кажуть про сайт. Не статичний аудит, а живі API-запити з web_search",
    status: "planned",
    effort: "M",
    impact: 5,
    note: "Killer-feature позиціонування. ~$0.20 за прогін з кешем 7д. Готовий план",
    docRef: "Власна пропозиція (не у спеці) — обговорено у чаті",
    details: `**Чим відрізняється від Шару 1:** Шар 1 каже "ти ДОЗВОЛИВ GPTBot читати сайт". Шар 2 каже "GPTBot РЕАЛЬНО знає про сайт і ось як він його описує".

Не статика — живі API-виклики до 4 провайдерів з ввімкненим web_search:
- Anthropic Claude (claude-opus-4-7 + web_search)
- OpenAI (gpt-4o-search-preview)
- Perplexity API
- Google Gemini (search grounding)

3 типи запитів × 4 LLM × кеш 7 днів:
- "Розкажи про romashka.co.ua" — чи знає про бренд
- "Знайди товари [категорія] на romashka.co.ua" — чи цитує реальні товари
- Для топ-3 PDP: "Розкажи про [product name] від Romashka" — чи описує точно

Score кожного запиту: cited / cited-with-error / not-cited. Для cited — accuracy опису (порівняння з реальним JSON-LD продукту).

Coverage: Perplexity і Gemini завжди мають свіжі дані; у Claude/OpenAI індекс іноді відстає на тижні. Saved snapshots показують власнику ДОСЛІВНІ цитати: "Ось що ChatGPT каже про твій сайт. А ось де він помилився".

Вартість ~$0.20 за повний прогін, кеш 7 днів = ~$1/місяць/tenant.`,
  },
  {
    id: "site-seo-ai-layer3",
    title: "SEO+AI · Шар 3: чи рекомендують LLM сайт",
    area: "site",
    description: "Monthly tracking: на запит «де купити X в Україні» — чи з'являється Romashka у топ-3 рекомендацій кожного LLM. Тренд по місяцях",
    status: "backlog",
    effort: "L",
    impact: 4,
    note: "Потрібна нова таблиця seo_ai_snapshots для трендів",
    docRef: "Власна пропозиція — Phase 3 SEO+AI",
    details: `**Чим відрізняється від Шару 2:** Шар 2 запитує "Розкажи про мій сайт" (LLM знає назву). Шар 3 запитує "Де купити жіночу піжаму в Україні?" — LLM сам має згадати бренд.

Найвищий шар цінності — чи рекомендують вас LLM коли клієнт НАВІТЬ НЕ знає назви бренду?

Щомісячно × 4 LLM × 5-10 категорійних запитів:
- "Де купити жіночу піжаму в Україні?"
- "Найкращі магазини шовкового одягу в Києві"
- "Бренди жіночого домашнього одягу Україна"

Tracking: чи появляється сайт у топ-3 / на якій позиції / як описаний / з якими конкурентами рекомендують поруч.

Тренд по місяцях показує чи Шар 1+2 фікси дають реальний приріст AI-цитувань. Якщо Romashka не у топ-3, а конкурент X — там, AI каже "конкурент має вищий impact по BP-CARTW-02 і BP-BOOST-04. Закрити gap?"

Storage: нова таблиця seo_ai_snapshots (period, query, llm, position, snippet, competitor_brands). ~30 записів/міс/tenant.`,
  },
  {
    id: "site-best-practices",
    title: "Каталог best-practices + per-tenant прогрес",
    area: "site",
    description: "75 пунктів CRO з категоріями + статус done/skipped + Director tools",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,    doneIn: "c32bd77 + 7f81d97",
  },
  {
    id: "site-create-tz-flow",
    title: "Wire \"Створити ТЗ\" на повний approval flow",
    area: "site",
    description: "Approval-request у Tasks UI → виконавець → effect-tracking",
    status: "partial",
    effort: "S",
    impact: 3,
    mvp: true,    note: "approval_request type=site_problem створюється, але tasks UI з ним поки не зв'язано",
    docRef: "Сайт-агент — Розділи 21-22 (як це бачить власник)",
    details: `Зараз кнопка "Створити ТЗ" на SiteAgentCard створює approval_request type=site_problem, але далі ланцюжок обірваний. Треба:

1. **Tasks UI** — окремий екран зі всіма approvals/задачами, фільтри (тип, агент, статус)
2. **Призначення виконавця** — підрядник / внутрішня команда / делегування на AI-агента
3. **Effect tracking** — після виконання baseline-snapshot, через 7 днів — порівняння

Формат картки задачі:
- Симптом (що саме знайшов агент)
- Що перевірити / що зробити
- Критерії приймання (об'єктивні: "step conversion add_to_cart > 4.5% за 7 днів після фіксу")
- Очікуваний ефект (UAH/місяць — детермінований)
- Прив'язка до конкретного BP-ID з каталогу best practices

Закриває петлю: агент знаходить → власник погоджує → виконавець робить → агент перевіряє ефект → DecisionLog фіксує "проблема X закрита, ефект +12 350 грн/міс підтверджено".`,
  },
  {
    id: "site-lighthouse-cli",
    title: "Server-side Lighthouse CLI замість PSI",
    area: "site",
    description: "Без квоти Google API, локально на сервері. ~5 хв одна перевірка",
    status: "backlog",
    effort: "M",
    impact: 2,
    note: "Опція якщо PSI 60с timeout не вистачає",
  },

  // ── Sales agent ──
  {
    id: "sales-keycrm",
    title: "KeyCRM інтеграція",
    area: "sales",
    description: "Inbound sync orders + leads + clients",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "08-03 phase",
  },
  {
    id: "sales-auto-sync-guards",
    title: "Auto-sync блокери (коментар / самовивіз / дублювання SKU)",
    area: "sales",
    description: "Орден з підозрілим патерном лишається оператору замість автопроведення",
    status: "done",
    effort: "S",
    impact: 3,
    mvp: true,    doneIn: "quick-260506-fmr",
    note: "Три блокери у /api/sales/orders/inbound: customer comment, pickup delivery, та (новий) сумарна кількість одного SKU > 1 (одна позиція з qty>=2 або кілька рядків того ж product_id)",
  },
  {
    id: "sales-customers-unified",
    title: "Уніфіковані Клієнти (phone/IG/email graph)",
    area: "sales",
    description: "Binotel + IG + email identifier graph + 530 records backfill",
    status: "done",
    effort: "XL",
    impact: 5,
    mvp: true,    doneIn: "agenthub_binotel_customers (3 фази)",
  },
  {
    id: "sales-call-transcription",
    title: "Транскрипція дзвінків + AI оцінка скриптів",
    area: "sales",
    description: "Whisper транскрипція + порівняння з call_scripts + рекомендації",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,    doneIn: "0032 + 0033 migrations",
  },
  {
    id: "sales-customer-summary",
    title: "AI customer summary",
    area: "sales",
    description: "Автогенерація профілю клієнта з усіх каналів",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "0033_customer_summaries",
  },
  {
    id: "sales-thread-status-ai",
    title: "AI-статуси діалогів (авто-детект + промпт на етап + таймлайн)",
    area: "sales",
    description:
      "Кожен статус несе промпт для ШІ; ШІ сам визначає етап діалогу та враховує його промпт у відповіді; таймлайн історії статусів над діалогом",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,
    doneIn: "quick-260610 thread-status-ai (6 commits)",
    note: "Опційний чекбокс auto-detect (default off). Класифікація через failover-чейн (web) + Haiku (worker). «Оплачено»/«Приховано» лишаються ручними. Історія у dm_thread_status_history (migration 0042) — живить майбутню аналітику. Romashka seeded 12 статусами з sales-script. + Галочка «Будь-яка оплата → Оплачено» біля статусу: payment sweep флипає на будь-який матчений платіж (навіть часткова передоплата 150 грн, без порогу 80%) і silently — без Задач та Telegram (tenant_meta paid_on_any_payment).",
  },
  {
    id: "sales-product-recognition",
    title: "Розпізнавання товару → блок «Обрані товари» → коректні відповіді про розмір/наявність",
    area: "sales",
    description:
      "ШІ розпізнає мод.ряд/артикул чи назву моделі з фото/скріну/шерд-поста клієнта → знаходить товар у каталозі → блок «Обрані товари» на діалозі → правильні відповіді про розмірну сітку/наявність (числа з каталогу).",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,
    doneIn: "27c89ef + f236a20 + 2055252 + c8e2c3c + 650d657 (recognizer)",
    docRef: ".planning/SPEC-product-recognition.md",
    note: "Ф1: фід emits <article>+<model_line> → колонки products (mig 0043) + findProductsByCode (article→варіант, model_line→весь ряд) + thread_selected_products (0044) + блок у IG-панелі + ін'єкція у generate. Ф2/3: vision (Haiku) для скрінів + failover для caption-reels; webhook вже віддає CDN-фото+підпис (IG Graph API НЕ потрібен) → recognizeProductOnInbound (код→точно / назва→пошук, conf≥60) auto-add source=ai; прапорець product_recognition_auto (default off, «🤖 авто» у блоці). Числа лише з каталогу. Verified: 2010611→4 кольори «Біссен», «Мелісса»→1010228, квитанція→0. Не верифіковано наживо лише: vision зчитування ВИДРУКУВАНОГО коду з реального фото (механізм перевірено, бракувало фото з кодом). Відповідь тепер ВЕДЕ діалог по розпізнаному товару (питає колір/розмір, називає наявність) і НІКОЛИ не каже «не можу відкрити Instagram/посилання». ПІДБІР РОЗМІРУ: заміри (довжина по спині/обхвати/рукав) яких немає у фіді — скрейпляться зі сторінки товару (feed <link>) per-size + кешуються (product_page_info, mig 0045); ШІ радить розмір за параметрами клієнта + параметрами моделі на фото. Verified: Дарока→XS|S/S|M/M|L заміри, AI рекомендує M|L. Deferred: worker reply path injection (не активний). АУДИТ 2026-06-24 (.planning/AUDIT-ig-product-pipeline.md) + P0-фікси 2026-06-25: (A) base-правило «уточню/перевірю» зроблено умовним — лише коли даних НЕМАЄ в блоках; (B) у блок «Обрані товари» додано Посилання на товар + Фото (раніше ШІ «не бачив посилання»); (C) один активний товар — нове розпізнавання прибирає попередні авто-здогадки (кінець плутанини Дарока+Магнолія). Verified e2e: ШІ дає посилання + точні см одразу, deflectить лише коли даних справді нема. P1 2026-06-25 (resolution-крок перед відповіддю): (D) визначає товар із сигналів — variation_id з лінка / код у тексті / LLM-назва моделі (з опискою «манголія»→Магнолія) → закріплює в «Обрані» або дає хінт дизамбігуації/уточнення; (E) текстова назва товару тригерить пошук БЕЗ кнопки. Статуси: resolved/ambiguous(питає X чи Y)/none(питає назву)/skip(привітання+колаборації не чіпає). + фікс WAF: скрейп замірів з прод-IP (DE) діставав 403 → accept/accept-language headers; error більше не кешується на 30д. + ціна: коли фід дає 0 — скрейпиться зі сторінки (JSON-LD), mig 0046. + ДВА РІВНІ товарів (mig 0047): кандидати (розглядає, P0-C один активний) → ПІДТВЕРДЖЕНІ (клієнт обрав — пін, не чиститься, КІЛЬКА для мульти-замовлення; для суми ШІ бере звідси). Підтвердження: кнопка ✅ у панелі + AI авто коли клієнт каже «беру/оформлюю». + UI 2 колонки (🔎 Пошук | ✅ Підтверджені) + підтверджені = позиції замовлення з обраним КОЛІР+РОЗМІР (dropdown + AI авто-захоплення з діалогу, mig 0048) + Сума замовлення. + AI ВИДАЛЕННЯ товарів на прохання клієнта (single/named детерміновано, мульти/«лиши тільки X» — LLM-резолвер). Ціна у GET/UI: каталог або скрейп.",
  },
  {
    id: "sales-ig-escalation",
    title: "IG: ескалація діалогу до живого менеджера (P2 аудиту)",
    area: "sales",
    description:
      "ШІ сам передає діалог менеджеру: холд треду (пауза) + Telegram-пінг власнику + одне прощальне повідомлення клієнту без продовження продажу",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,
    doneIn: "ig-escalation P2 (2026-07-02)",
    docRef: ".planning/AUDIT-ig-product-pipeline.md",
    note: "Тригери (детерміновані regex, без LLM, лише останнє вхідне): (1) клієнт просить живу людину/менеджера/оператора або питає «ви бот?»; (2) сильний негатив/скарга (обман, поверніть гроші, поліція/юрист); (3) поза каталогом — повернення/обмін/індивідуальне пошиття/опт; (4) ≥3 послідовних невдалих resolution товару (лічильник resolution_fail_count, «resolved» скидає). Дія: dm_thread_reads.paused_at (існуючий холд — авто-відповіді його вже поважають) + escalated_at/escalation_reason (mig 0049) + Telegram пінг (клієнт/причина/останнє повідомлення/лінк на панель, purpose=other) + hint у промпт «передай менеджеру, не продовжуй продаж». Ідемпотентно: поки escalated_at стоїть — без повторних пінгів. Зняття паузи в панелі скидає ескалацію+лічильник (менеджер розібрався). Verified tsx-симуляцією: 13/13 тригер-кейсів + повний цикл + Telegram delivery ok.",
  },
  {
    id: "sales-workspace-tabs",
    title: "Sales workspace (Inbox / Site Orders / Follow-up / Training)",
    area: "sales",
    description: "4 вкладки для менеджера в одному екрані",
    status: "planned",
    effort: "L",
    impact: 4,
    note: "OneBox order create MVP є, але повноцінного workspace-а з 4 tabs немає",
    docRef: "Розділ 12 (Sales Workspace) + ВП block 8.2",
    details: `AI Sales Workspace — окремий екран для менеджера, 4 вкладки:

**Inbox / Queue** — таблиця діалогів: канал (IG/сайт/TG), пріоритет, статус (draft ready / needs approval / human needed), AI confidence %, дія. Кнопки на рядку: відправити / редагувати / передати людині / "хороший приклад" / "поганий приклад" / "додати правило".

**Site Orders** — нові замовлення з сайту: оплата є/немає, остання одиниця, дефект/перевірка, стандартне vs нестандартне, рекомендована дія.

**Follow-up Board** — блоки: не оплатив, не відповів, покинутий кошик, накладений платіж, передзамовлення, повернення/обмін.

**Training** — good replies / bad replies / tone of voice / FAQ / стоп-фрази / ескалації / нові правила / історія змін. Менеджер тренує агента прямо тут — кожен "поганий приклад" стає правилом.`,
  },
  {
    id: "sales-modes-a0-a2",
    title: "Режими роботи A0/A1/A2/Human",
    area: "sales",
    description: "Тіньовий → Співпілот → Контрольований auto → Manual",
    status: "partial",
    effort: "M",
    impact: 4,
    mvp: true,    note: "IG suggest mode є, треба формалізувати state-machine та UI перемикач",
    docRef: "Розділ 13 (Режими роботи агента продажів)",
    details: `Чотири стани з різним балансом AI ↔ людина:

- **A0 / Recommendation** — AI радить, показує процеси. Рішення приймає людина.
- **Shadow** — AI готує дію, але не відправляє. Людина перевіряє і відправляє вручну.
- **A1 / Copilot** — AI готує дію на погодження. Людина погоджує / редагує / відхиляє.
- **A2 / Controlled Autopilot** — AI сам виконує безпечні сценарії. Людина бачить звіт.
- **Human** — AI не діє. Людина повністю керує.

Romashka хоче A2 одразу або швидко (через 7-14 днів). Для нових клієнтів стандарт = Shadow → A1 → A2 (поетапно, на основі trust score).

Перемикач режиму на рівні агента (не глобально) і на рівні сценарію (можна зробити A2 для типових замовлень, але A1 для повернень). Стан зберігається в agent_settings.execution_mode.`,
  },
  {
    id: "sales-manager-tg-flow",
    title: "Telegram-flow менеджера (start-shift, queue, dialogue intervention)",
    area: "sales",
    description: "/me start, /pick top item, AI підказує наступну дію в діалозі",
    status: "planned",
    effort: "L",
    impact: 4,
    note: "Telegram bot є, але manager-flow ще не виділений",
    docRef: "Розділ 15 (Telegram-flow менеджера) + ВП block 7.2",
    details: `Менеджер працює зі своєї зміни через Telegram, без необхідності постійно бути у вебі.

Початок зміни: /me start — система привітає, покаже скільки в черзі, які пріоритети, що нового з минулої зміни.

В черзі: /next або кнопка "Взяти наступний" → AI відкриває діалог з клієнтом + draft-відповідь. Менеджер може:
- надіслати як є (1 клік)
- редагувати → надіслати
- передати назад в чергу
- ескалувати власнику

Дія в діалозі: AI підказує наступний крок ("клієнт питає про розмір 38, на складі 2 шт, рекомендую запропонувати 36 або 40"). Менеджер обирає або відхиляє.

Кінець зміни: /me end — автоматичний звіт скільки замовлень, який AOV, % AI-coverage, найкращі кейси.`,
  },

  // ── Analytics ──
  {
    id: "analytics-panel",
    title: "Analytics panel + GA4/FB Ads dashboard",
    area: "analytics",
    description: "Overview · Funnel · Sources · Pages · FB Ads campaigns",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,    doneIn: "components/agent-panels/analytics-panel + site/analytics tab",
  },
  {
    id: "analytics-clarity",
    title: "Clarity NL API integration",
    area: "analytics",
    description: "JS errors / rage clicks / dead clicks / quick backs · 7-day window",
    status: "done",
    effort: "M",
    impact: 3,
    mvp: true,    doneIn: "agenthub_site_funnel_clarity",
  },
  {
    id: "analytics-kpi-hub",
    title: "Окремий KPI Hub з drill-down",
    area: "analytics",
    description: "Топ-вью по всіх KPI з clickable cells → причини змін",
    status: "planned",
    effort: "L",
    impact: 3,
    note: "Зараз KPI розкидані по екранах. Хочеться single KPI dashboard",
    docRef: "Аналітик block — Розділ 10 (KPI-блоки за напрямками)",
    details: `Окремий екран зі всіма ключовими показниками згрупованими по напрямках:

**Загальна панель бізнесу** — валовий дохід, чистий прибуток, маржа, виконання плану, опер.витрати/дохід, маркетинг/дохід, закупка/дохід, залишок товарів грн, нові/старі клієнти, повторні продажі.

**Товар / закупка** — A+/A/B/C/D наявність, мертвий склад, середня вартість 1 товару, витрати на закупку, % від доходу, оборотність у днях/циклах, ліквідність складу (місяці).

**Продажі** — прийняті vs закриті vs відмови (грн і шт), середній чек, довжина чеку, допродаж, конверсія замовлення → виконання.

**Незабори** — з передплатою, без передплати, по каналах (сайт/IG/офлайн/маркетплейси), витрати на зворотну логістику, врятований дохід після нагадування.

**Клієнти** — перші vs повторні продажі, нові, retention, NPS.

Кожна клітинка clickable — drill-down "Чому виросло/впало" з порівнянням періодів і знайденими причинами (через causal engine).`,
  },
  {
    id: "analytics-causal",
    title: "Причинно-наслідковий аналіз",
    area: "analytics",
    description: "Клік на аномалію → Director шукає корелювані події (FB кампанія старт, ціна змінилась, NP затримка)",
    status: "planned",
    effort: "L",
    impact: 4,
    note: "Anomaly detection є — треба + correlation engine на metric_snapshots",
    docRef: "Аналітик block — Розділ 5 (Причинно-наслідковий аналіз)",
    details: `Аналітик не просто показує що "продажі впали". Він пояснює чому, спираючись на корельовані події у системі.

Алгоритм:
1. Аномалія детектується z-score на metric_snapshots
2. Аналітик шукає події у вікні ±3 дні: FB кампанія старт/стоп, зміна ціни в CRM, NP затримка > X днів, новий конкурент згаданий у IG, погода, свято
3. Ранжує гіпотези за силою кореляції
4. Видає висновок: "Замовлення впали на 23% за 3 дні. Найімовірніша причина — старт нової FB кампанії 2 дні тому з 3× нижчим CTR (видно у FB Ads). Альтернативна гіпотеза — підвищення цін на 15% у топ-категорії 1 день тому."

Storage: розширити metric_snapshots тегами event-кореляцій. Або окрема таблиця events_log яка обʼєднує signal-события з усіх інтеграцій.

Цінність: власник перестає здогадуватись. Кожна аномалія приходить з 1-2 підтвердженими гіпотезами і пропозицією дії.`,
  },

  // ── Buyer agent ──
  {
    id: "buyer-mvp",
    title: "Buyer MVP — режим видимості",
    area: "buyer",
    description: "Спостерігає закупки + збирає історію без втручань",
    status: "backlog",
    effort: "L",
    impact: 4,
    note: "Закупівельник з doc'у — повністю не стартовано",
    docRef: "Закупщик block — Розділи 1-10",
    details: `Перші 1-3 дні — режим видимості: спостерігає закупки + збирає історію. Без втручань.

Дані які треба зібрати:
- Топ-постачальники: % від загальної закупки, середній термін, % браку
- Cycle time: від замовлення до приходу, від приходу до продажу
- Mortal stock: товари що не продавались > 30 днів
- ABC-аналіз: A+ (хіти), A (сильні), B (середні), C (слабкі), D (зомбі)
- Сезонність: продажі за місяцями, тренди

Дні 4-7 — режим співпілота: пропонує що замовити + чорнетка PO, власник підтверджує.

Дні 8-14 — контрольовані рекомендації: AI каже "топ-3 товари закінчаться через 5-7 днів, рекомендую замовити X штук від постачальника Y, бо у нього найкращий cycle time + ціна".

Особлива користь Romashka — вона часто працює без CRM закупки. Закупщик тут — це і CRM Lite одночасно.`,
  },
  {
    id: "buyer-demand-forecast",
    title: "Прогноз попиту + матриця постачальників",
    area: "buyer",
    description: "Топ-N товарів які закінчаться через X днів + кращий постачальник",
    status: "backlog",
    effort: "XL",
    impact: 4,
    note: "Залежить від Buyer MVP",
    docRef: "Закупщик block — Розділи 11-13",
    details: `Прогноз стоків з урахуванням:
- Поточний залишок
- Швидкість продажу (середня за 30 днів)
- Сезонність (порівняно з минулим роком)
- Cycle time постачальника (час від замовлення до приходу)

Формула: days_until_oos = stock / daily_velocity. Якщо < cycle_time + safety_buffer → треба замовляти зараз.

Матриця постачальників — ранжування за: ціна, термін, % браку, гнучкість (мінімальне замовлення), історія співпраці. Для кожного товару рекомендується найкращий постачальник.

Топ-3 товари які потребують термінового перезамовлення показуються щодня в директорському Morning Brief. Якщо нічого критичного — нагадування мовчить.

XL effort бо потрібно: підключитись до 1С/Excel/CRM як джерела поточних залишків, накопичити 30+ днів історії продажів, побудувати матрицю постачальників.`,
  },
  {
    id: "buyer-co-pilot",
    title: "Buyer Co-pilot — режим співпілота",
    area: "buyer",
    description: "Пропонує що замовити + чорнетка PO. Власник підтверджує",
    status: "backlog",
    effort: "L",
    impact: 4,
    docRef: "Закупщик block — Розділ 10 (День 4-7)",
    details: `Після 1-3 днів видимості, агент починає пропонувати дії:
- "Топ-3 товари критичні: A, B, C — замовити X / Y / Z штук"
- "Постачальник P не виходив на зв'язок 5 днів — треба нагадати або шукати альтернативу"
- "Категорія K показує −20% продажів 2 тижні поспіль — рекомендую не дозамовляти, дочекатись розпродажу"

Кожна дія приходить з approval-кнопками: погодити / відкласти / відхилити з причиною.

Підтверджені дії автоматично формують PO (purchase order) — текст для відправки постачальнику в Telegram/email/SMS. Власник отримує draft, редагує і відправляє одним кліком.

Прогрес-метрики агента: % прийнятих рекомендацій, заощаджений час власника, % перевиконання прогнозу попиту, скорочення mortal stock.`,
  },

  // ── HR ──
  {
    id: "hr-mvp",
    title: "HR-агент — режим видимості",
    area: "hr",
    description: "Команда / ролі / KPI · базова видимість",
    status: "partial",
    effort: "M",
    impact: 3,
    note: "HR employees migration (0035) є, базова сторінка теж — треба брифінг + KPI",
    docRef: "HR block — Розділи 1-7",
    details: `HR-агент починає з режиму видимості: збирає базову структуру команди без втручань.

Брифінг (8 питань 7-10 хв):
- Скільки людей у команді?
- Які ролі (продавці, оператори, склад, керівники)?
- Хто за кого відповідає?
- Які KPI кожної ролі?
- Хто з ключових на одну людину (bus factor 1)?
- Текучка останні 6 міс — вище чи нижче за нормальну?
- Які процеси найбільш ручні?
- На кого ти найбільше витрачаєш енергії як власник?

Після цього система формує:
- Карту команди з ролями
- Ризики bus factor 1
- Найбільш ручні процеси (кандидати на автоматизацію)
- Топ-3 болі власника по команді

Romashka ймовірно: 8-12 людей, 4-5 ролей, 2-3 ключові на одній людині. Перша швидка перемога — чітко описати hand-off між ролями (зараз "усі знають все", але це і є хаос).`,
  },
  {
    id: "hr-team-quality",
    title: "Оцінка якості команди",
    area: "hr",
    description: "Скрипти / NPS / response time / sales conversion per manager",
    status: "planned",
    effort: "L",
    impact: 3,
    note: "AI-оцінка скриптів вже є — треба згрупувати по менеджерах",
    docRef: "HR block — Розділи 8-13",
    details: `Кожен менеджер отримує "паспорт" з KPI:
- Кількість оброблених замовлень
- Sales conversion (оброблено → закрито)
- Середній чек
- Скільки разів AI втрутився у діалог
- % правильних відповідей за скриптом (вже є AI-оцінка скриптів)
- Середній response time
- NPS після спілкування (якщо збираємо)
- Кількість ескалацій до власника
- Відмови / повернення обумовлені помилкою менеджера

Bonus — топ-фрази які працюють краще за середнє (witnessed conversion lift). Можна тренувати інших на найкращих прикладах.

Власник отримує weekly review: "цього тижня менеджер A показав найкращу конверсію (47%, +12% vs тижня тому), B — найгіршу (31%, треба тренінг по наполегливості)".`,
  },

  // ── Recruiter ──
  {
    id: "recruiter-mvp",
    title: "AI-рекрутер — публікація вакансій + воронка кандидатів",
    area: "recruiter",
    description: "Текст вакансії + збір CV + базовий ранжуван",
    status: "backlog",
    effort: "L",
    impact: 3,
    docRef: "Рекрутер block — Розділи 1-14",
    details: `Перша мікросесія агента (10-15 хв):
- Хто потрібен (роль, рівень, унікальні вимоги)
- Чому потрібен (заміна, ріст, новий напрямок)
- Терміновість (вчора / місяць / щоквартально)
- Бюджет ЗП (вилка)
- Що відрізняє цю компанію (для тексту вакансії)
- Хто буде фінальний рішатель (власник / тимлід)

Артефакти які створює:
- Опис вакансії (4-5 версій під різні платформи)
- Список питань для скринінгу
- Кваліфікаційний скоркард
- Воронка джерел: Work.ua / Robota.ua / Telegram канали / реферали

Воронка найму (без відео-інтерв'ю в цій версії):
- Заявка → CV-скоринг (AI) → телефонний скринінг → текстове інтерв'ю → фінал з власником

Цінність: власник перестає сам писати вакансії і вичитувати 50 CV. Отримує готових топ-3 кандидати з обґрунтуванням.`,
  },
  {
    id: "recruiter-video-interview",
    title: "Відео-інтерв'ю + AI-оцінка",
    area: "recruiter",
    description: "Структуровані 5 питань + AI-аналіз відео + картка кандидата",
    status: "backlog",
    effort: "XL",
    impact: 4,
    docRef: "Рекрутер block — Розділи 11-12",
    details: `Кандидат отримує лінк, записує 5 коротких відео (1-2 хв кожне) на структуровані питання:
- Розкажи про себе (1 хв) — оцінка комунікації
- Найскладніший кейс у попередній роботі (2 хв) — STAR-структура
- Чому саме ця посада (1 хв) — мотивація
- Питання за стандартним сценарієм Romashka (2 хв) — рольова гра
- Що ти зробиш у перші 30 днів (1 хв) — цілевизначеність

AI-аналіз відео:
- Транскрипція + структура відповіді (логіка / приклади / висновки)
- Tone of voice (впевненість, теплота, енергія)
- Конкретика (числа vs загальні слова)
- Невербальні сигнали (опціонально, на основі pose detection)

Картка кандидата зі скором по кожному виміру + cherry-picked moments + рекомендація "запросити на фінал" / "відмовити" / "borderline".

XL effort бо: відео storage + transcription pipeline + multi-modal LLM аналіз + UI з вбудованим player + privacy-compliance (UA + EU).`,
  },

  // ── Content ──
  {
    id: "content-jobs",
    title: "Content jobs (text+image generation)",
    area: "content",
    description: "Картки товарів · banners · IG posts · продуктові тексти",
    status: "partial",
    effort: "M",
    impact: 3,
    note: "Базовий content/jobs API є — треба UI keypipeline + готові preset-и",
  },
  {
    id: "content-ig-posts",
    title: "IG posts автогенерація + scheduler",
    area: "content",
    description: "Reels-ідеї + drafts + Mediа Insights → коли публікувати",
    status: "backlog",
    effort: "L",
    impact: 3,
  },

  // ── Approvals + Telegram ──
  {
    id: "approvals-queue",
    title: "Approvals queue UI",
    area: "approvals",
    description: "Окремий screen зі всіма pending approval_requests + filter + approve/reject",
    status: "partial",
    effort: "M",
    impact: 4,
    mvp: true,    note: "approval_requests таблиця є, базовий UI — треба повний editor + history",
    docRef: "Director 2.0 — Розділ 20 (Approval-flow)",
    details: `Кожен approval має 4 поля які власник бачить ОДРАЗУ:
- Що саме AI хоче зробити (одне речення)
- Очікуваний ефект (з числами: -25-35 хв ручної роботи / +X грн / -Y повернень)
- Ризик (низький / середній / високий + пояснення)
- Умова (де саме AI втрутиться: "тільки типові замовлення без конфліктів")

4 кнопки: погодити / відкласти / поясни детальніше / заборонити для цього сценарію.

UI screen: список з фільтрами (агент, ризик, тип, agent_role), сортування за impact, batch-actions (погодити всі низько-ризикові). Розгорнутий вид показує evidence (звідки AI взяв висновок: GA4, Clarity, customer messages, тощо).

History: кожне approval/rejection йде у DecisionLog з результатом через 7 днів (effect-tracking) — щоб власник бачив свою точність ("ти схвалив 23 з 25, 21 дала очікуваний ефект, 2 — гірше ніж AI прогнозував").`,
  },
  {
    id: "approvals-telegram-buttons",
    title: "Telegram buttons для approval",
    area: "approvals",
    description: "Inline-кнопки «Apply / Reject / Show evidence» прямо у TG",
    status: "partial",
    effort: "S",
    impact: 4,
    mvp: true,    note: "Базові notifications є, треба inline keyboard + callback handlers",
    docRef: "Telegram-flow власника — Розділи 14, 19",
    details: `Власник 80% часу не у вебі. Approval має приходити в Telegram з inline keyboard щоб погодити одним кліком:

Формат повідомлення:
🔵 Агент продажів пропонує: перевести нагадування про оплату в автоматичний режим.
📊 Ефект: -25-35 хв/день
⚠️ Ризик: низький
🎯 Умова: лише типові замовлення без конфліктів

[ Погодити ] [ Відкласти ] [ Деталі ] [ Заборонити ]

При натисканні: callback handler виконує дію + надсилає підтвердження. Edit_message прибирає кнопки і ставить ✅ "Погоджено о 14:32 → ефект буде заміряний через 7 днів".

Edit message не тригерить notification, але результат через 7 днів приходить новим повідомленням.`,
  },
  {
    id: "approvals-effect-tracking",
    title: "Tracking ефекту approved дії",
    area: "approvals",
    description: "Approved → дія → метрики через 7 днів → автоматично у журнал",
    status: "planned",
    effort: "M",
    impact: 4,
    docRef: "Director 2.0 — Розділ 30 (DecisionLog)",
    details: `Для кожного approved approval-а зберігається baseline-snapshot метрик ДО (median за 7 днів). Через 7 днів cron порівнює нові 7 днів з baseline і обчислює:
- Чи був заявлений ефект досягнутий
- На скільки % відхилення від прогнозу
- Чи є побічні ефекти (інші метрики що змінились)

Результат пишеться в DecisionLog. Через місяць власник може подивитись:
- Скільки approval-ів я схвалив
- Який % з них дав очікуваний ефект
- Які типи дій найбільш точні (агент продажів краще передбачає чи агент сайту?)
- Сумарний бенефіт (грн, хвилини, конверсії)

Це закриває петлю довіри: AI не просто пропонує, AI вчиться які пропозиції власник схвалить, які дадуть очікуваний ефект.`,
  },

  // ── Memory ──
  {
    id: "memory-business-dna",
    title: "business_dna.md (живий паспорт)",
    area: "memory",
    description: "Markdown файл з усім що Director знає про бренд",
    status: "done",
    effort: "M",
    impact: 5,
    mvp: true,    doneIn: "Director M1",
  },
  {
    id: "memory-knowledge-files",
    title: "Knowledge files з RAG",
    area: "memory",
    description: "knowledge_chunks + embeddings → semantic retrieval",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,    doneIn: "knowledge_files migrations",
  },
  {
    id: "memory-integration-knowledge",
    title: "Довідник інтеграцій (per-agent)",
    area: "memory",
    description: "Shared guide на 21 інтеграцію + per-agent style tab",
    status: "done",
    effort: "M",
    impact: 3,
    mvp: true,    doneIn: "0036_integration_knowledge",
  },

  // ── Integrations ──
  {
    id: "int-ga4",
    title: "GA4 OAuth + Data API",
    area: "integrations",
    description: "Property picker + одноразовий OAuth + усі fetcher-и",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "85e5207 GA4 one-click",
  },
  {
    id: "int-ga4-shared-sa",
    title: "GA4 підключення «дай доступ нашому email»",
    area: "integrations",
    description: "Спільний платформний service account: клієнт додає наш email як Viewer (2 кліки) + авто-підбір властивостей через Admin API",
    status: "partial",
    effort: "M",
    impact: 4,
    doneIn: "308ad90 (Variant A)",
    note: "Код готовий, дормантний поки не заданий GA4_PLATFORM_SA_JSON. Потрібен разовий сетап платформного SA (Data+Admin API) у нашому Google Cloud",
  },
  {
    id: "int-fb-ads",
    title: "FB Ads insights",
    area: "integrations",
    description: "Ad insights + campaigns + ROAS",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "fb-ads service",
  },
  {
    id: "int-instagram",
    title: "Instagram DM (data-unity proxy)",
    area: "integrations",
    description: "OAuth + page picker + subscribe + webhook + send",
    status: "done",
    effort: "XL",
    impact: 5,
    mvp: true,    doneIn: "agenthub_session_handoff_2026_05_04",
  },
  {
    id: "int-binotel",
    title: "Binotel telephony + webhook",
    area: "integrations",
    description: "Call settings lookup + call completed upsert + audio player",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,    doneIn: "agenthub_binotel_customers",
  },
  {
    id: "int-monobank",
    title: "Monobank acquiring + top-up",
    area: "integrations",
    description: "ECDSA verified + USD billing flow",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,    doneIn: "agenthub_balance_monobank_browse",
  },
  {
    id: "int-privatbank",
    title: "PrivatBank Авто-клієнт API",
    area: "integrations",
    description: "Multi-token + multi-account + transactions cache",
    status: "done",
    effort: "L",
    impact: 4,
    doneIn: "agenthub_privatbank_finance + 0038",
  },
  {
    id: "int-novaposhta",
    title: "Nova Poshta multi-account",
    area: "integrations",
    description: "Primary MCP + extras JSON + direct API client + tracking",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "agenthub_novaposhta_multi",
  },
  {
    id: "int-onebox",
    title: "OneBox CRM (POST orders)",
    area: "integrations",
    description: "AI-extracted order_drafts → POST у CRM",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "agenthub_order_drafts",
  },
  {
    id: "int-clarity-mcp",
    title: "Microsoft Clarity MCP",
    area: "integrations",
    description: "NL queries для UX-сигналів",
    status: "done",
    effort: "M",
    impact: 3,
  },
  {
    id: "int-swagger-mcp",
    title: "Swagger/OpenAPI MCP framework",
    area: "integrations",
    description: "Універсальний spec → tools generator (Sitniks, SalesDrive, KeyCRM, OneBox)",
    status: "done",
    effort: "L",
    impact: 4,
    doneIn: "agenthub_swagger_mcp",
  },

  // ── Schedules ──
  {
    id: "schedule-skills",
    title: "Cron-skills + UI editor",
    area: "schedules",
    description: "Створити / редагувати / поставити anomaly-only / видалити",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,    doneIn: "3b1dcd0 + 18eb9cb",
  },
  {
    id: "schedule-monitor",
    title: "Active monitor (4-h)",
    area: "schedules",
    description: "Heartbeat кожні 15 хв, monitor кожні N год, anomaly-only Telegram",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "agenthub_director_verification_monitor",
  },
  {
    id: "schedule-weekly-reflection",
    title: "Weekly reflection skill",
    area: "schedules",
    description: "Щонеділі 19:00 — reflections/week_<N>.md + оновлення business_dna",
    status: "done",
    effort: "S",
    impact: 3,
    mvp: true,    doneIn: "agenthub_director_self_aware",
  },
  {
    id: "schedule-daily-snapshot",
    title: "Daily KPI snapshot",
    area: "schedules",
    description: "06:15 Київ — yesterday's KPI у metric_snapshots для z-score",
    status: "done",
    effort: "S",
    impact: 4,
    mvp: true,    doneIn: "Director M4",
  },

  // ─── Buyer (Закупівельник) — повна серія зі спеки (26 розділів) ─────────
  {
    id: "buyer-onboarding-mikrosession",
    title: "Buyer: 7-хв брифінг закупки через SPIN",
    area: "buyer",
    description: "Перша мікросесія з власником: 8 питань — як зараз закуповує, які болі з товаром, які постачальники, що зависло, що горить",
    status: "backlog",
    effort: "M",
    impact: 4,
    docRef: "Закупщик block — Розділ 6.1",
    details: `Перша мікросесія агента закупки 7-10 хв з 8 питаннями SPIN-логіки:

1. Звідки ти зараз береш картину запасів? (Excel / 1С / CRM / голова)
2. Що саме ти бачиш найгірше: брак / надлишок / повільний обіг?
3. Скільки постачальників і хто ключові — 80% обороту тримають скільки?
4. Яких товарів НЕ повинно ніколи закінчуватись?
5. Як часто ти переплановуєш бюджет закупки?
6. Скільки часу ти витрачаєш на закупку (год/тиждень)?
7. Що б змінилось якби раз на день мав чесний звіт по запасах?
8. Дозволиш агенту допомогти приймати дрібні рішення коли він проявить себе?

Після відповідей формується перший profile + готовність до щоденного брифу.`,
  },
  {
    id: "buyer-morning-brief",
    title: "Buyer: ранковий звіт по запасах",
    area: "buyer",
    description: "Щодня 9:00 — топ-маржа · ризик нестачі (14 і 30 днів) · заморожені гроші · оцінка новинок · 3 рекомендації дій",
    status: "backlog",
    effort: "M",
    impact: 5,
    docRef: "Закупщик block — Розділ 14 (Telegram)",
    details: `Формат щоденного повідомлення власнику:

«Доброго ранку. Коротко по асортименту за вчора:
- 5 позицій дали найбільшу маржу
- 7 важливих позицій мають ризик нестачі на 14 днів
- 12 позицій тримають занадто багато грошей без обігу
- 3 новинки зайшли добре, 4 слабкі

Що раджу сьогодні:
1. Перевірити 7 позицій із ризиком нестачі
2. Погодити список з 12 позицій на детальний перегляд
3. Подивитись оцінку 4 слабких новинок»

Кнопки швидких дій: ранковий звіт / 14 днів / 30 днів / заморожені гроші / новинки / розпродаж / поясни / навчити агента.`,
  },
  {
    id: "buyer-frozen-money",
    title: "Buyer: блок «Заморожені гроші»",
    area: "buyer",
    description: "Топ-N позицій що лежать > 30/60/90 днів. Сума заморожених грошей у грн. Рекомендація: розпродаж / уцінка / списання",
    status: "backlog",
    effort: "M",
    impact: 4,
    docRef: "Закупщик block — Розділи 5.2, 13",
    details: `Топ-N товарів які лежать > 30/60/90 днів без обігу. Сума заморожених грошей у грн (наскільки кеш «спить» на складі).

Рекомендована дія для кожного товару:
- > 90 днів і слабка категорія → списання / донація
- 60-90 днів → розпродаж зі знижкою 30-50%
- 30-60 днів → промо / контент-просування

Власник бачить: "У вас 1.2 млн грн заморожено в C-D товарах. Можемо звільнити ~600к через розпродаж — погоджуєш?".`,  },
  {
    id: "buyer-stockout-risk",
    title: "Buyer: попередження про нестачу 14/30 днів",
    area: "buyer",
    description: "Список товарів з days_until_oos < cycle_time + safety_buffer. Ранжовані за критичністю (топ-маржа + швидкість обігу)",
    status: "backlog",
    effort: "M",
    impact: 5,
    docRef: "Закупщик block — Розділ 5.2",
    details: `Список товарів з days_until_oos < cycle_time + safety_buffer. Формула: stock / daily_velocity.

Ранжування за критичністю:
- Топ-маржа × частота продажу → найкритичніше
- Сезонні піки враховуються (порівняно з минулим роком)
- Хіти (A+) у червоній зоні підіймаються найвище

Якщо bestseller закінчиться через 5 днів і cycle_time постачальника 7 днів — alert у Telegram. Власник погоджує перезамовлення одним кліком.`,  },
  {
    id: "buyer-newcomers",
    title: "Buyer: оцінка новинок",
    area: "buyer",
    description: "Товари заведені < 30 днів — як зайшли. velocity vs benchmark категорії. Ранній сигнал: підсилити маркетинг або зняти з закупки",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Закупщик block — Розділ 13",
    details: `Товари заведені < 30 днів — оцінка "як зайшли".

Метрики:
- Velocity vs benchmark категорії
- Sell-through (% проданого від першого приходу)
- Маржа vs середня по бренду
- Контент-engagement (якщо доступно з IG)

Ранній сигнал на 7-14 день: підсилити маркетинг, переоцінити, або зняти з закупки. Уникає ситуації коли власник через 3 місяці бачить що 40% новинок не зайшли — і вже пізно реагувати.`,  },
  {
    id: "buyer-supplier-rating",
    title: "Buyer: рейтинг постачальників",
    area: "buyer",
    description: "Матриця: ціна · cycle time · % браку · гнучкість · історія співпраці. На кожен товар найкращий постачальник",
    status: "backlog",
    effort: "L",
    impact: 4,
    docRef: "Закупщик block — Розділи 11-13",
    details: `Матриця-скоркард по кожному постачальнику:
- Ціна (vs альтернативи)
- Cycle time (час від замовлення до приходу)
- % браку
- Гнучкість (мінімальне замовлення, можливість partial-fulfillment)
- Дисципліна (% вчасних поставок)
- Історія співпраці

На кожен товар рекомендується найкращий постачальник за зваженою формулою. Власник бачить: "Цей товар у Постачальника А коштує -8% і йде на 3 дні швидше — варто перевести".`,  },
  {
    id: "buyer-budget-discipline",
    title: "Buyer: бюджет закупки + дисципліна виконання",
    area: "buyer",
    description: "План vs факт по місяцях. Сигнал якщо overspend / underspend > X%",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Закупщик block — Розділ 13",
    details: `План закупки на місяць (вводиться власником або генерується від прогнозу попиту) vs факт виконання.

Ключові алерти:
- Overspend > 10% → дисципліна порушена
- Underspend > 15% → ризик нестачі
- Перерозподіл між категоріями vs плану

Звіт показує не лише суму, а й що саме купили нестандартно — "ти планував 30к на A-категорію, купив 50к; на B недокупив 20к — ризик нестачі".`,  },
  {
    id: "buyer-tg-buyer-channel",
    title: "Buyer: окремий Telegram-канал закупщика",
    area: "buyer",
    description: "Спрощений flow для закупщика-людини: щоденний список / погодити PO / прийняти товар / зафіксувати брак",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Закупщик block — Розділ 15",
    details: `Спрощений Telegram-flow для людини-закупщика (не власника):
- /shift start — початок зміни, що в черзі
- Щоденний список: "сьогодні треба замовити X, Y, Z"
- Підтвердити PO одним кліком
- Зафіксувати приймання товару (photo + кількість)
- Зафіксувати брак з фото

Окремий доступ — закупщик не бачить фінансові summary власника, тільки операційні задачі по товарах.`,  },
  {
    id: "buyer-web-owner",
    title: "Buyer: web-сторінка власника",
    area: "buyer",
    description: "Огляд асортименту в цифрах: A+/A/B/C/D, оборотність, ліквідність складу (місяці), графіки сезонності",
    status: "backlog",
    effort: "L",
    impact: 4,
    docRef: "Закупщик block — Розділ 16",
    details: `Web-сторінка власника з повним оглядом асортименту:
- ABC-розподіл (A+/A/B/C/D наявність + продажі)
- Графік оборотності за 12 місяців
- Mortal stock breakdown по категоріях
- Сезонні криві (порівняння з минулим роком)
- Ліквідність складу у місяцях ("на скільки вистачить запасів")
- План закупки vs факт

Drill-down на кожний товар: історія цін, рух залишків, рекомендації агента.`,  },
  {
    id: "buyer-director-questions",
    title: "Buyer: інтеграція з директором (questions after)",
    area: "buyer",
    description: "Після Buyer власник отримує від Director питання типу «закрити мертвий C-склад на 50% — це звільнить ~Х тис, погоджуєш?»",
    status: "backlog",
    effort: "S",
    impact: 3,
    docRef: "Закупщик block — Розділи 11-12",
    details: `Після того як buyer-агент знайшов інсайти, Director у щоденному діалозі ставить власнику конкретні рішення:

«Закупщик пропонує закрити мертвий C-склад на 50% — це звільнить ~600к грн готівки. Ризик: можемо втратити ~30к маржі від випадкових повернень. Погоджуєш?»

«15 хітів у червоній зоні запасів. Постачальник А має cycle_time 7 днів. Замовити стандартний обсяг чи 1.5×? (1.5× = безпечніше але +200к заморожених грошей).»

Approval-flow з Phase 13 інваріантами.`,  },

  // ─── HR — повна серія (26 розділів) ────────────────────────────────────
  {
    id: "hr-onboarding-mikrosession",
    title: "HR: 7-хв брифінг команди",
    area: "hr",
    description: "8 питань: розмір команди / ролі / hand-off / KPI / bus factor 1 / текучка / ручні процеси / болі власника",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "HR block — Розділ 6.1",
    details: `Перша мікросесія HR-агента 7-10 хв з 8 питаннями:
1. Скільки людей у команді? (1-3 / 4-10 / 11-30 / 30+)
2. Які ролі (продавці, оператори, склад, керівники)?
3. Хто за кого відповідає, де є hand-off?
4. Які KPI кожної ролі?
5. Хто з ключових — на одній людині (bus factor 1)?
6. Текучка останні 6 міс — нормальна чи висока?
7. Які процеси найбільш ручні?
8. На кого ти найбільше витрачаєш енергії як власник?

Виводить: карту команди, bus-factor ризики, top-3 болі власника, кандидатів на автоматизацію.`,  },
  {
    id: "hr-team-map",
    title: "HR: карта команди + bus factor",
    area: "hr",
    description: "Візуалізація хто за кого відповідає, де hand-off, які ролі мають bus factor 1 (ризик якщо ця людина зникне)",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "HR block — Розділи 5.2, 9",
    details: `Візуальна мапа команди:
- Кружечки — люди, лінії — hand-off (хто кому передає роботу)
- Кольори ролей: продажі / склад / контент / керівники
- Червоні мітки на bus factor 1 (людина без бекапу)
- Розмір кружечка = % робочого часу що ця роль забирає у команди

Bus-factor алерт: "Якщо Аліна зникне на 2 тижні — продажі впадуть на 40%. Треба або дублікат ролі, або перетягнути частину навантаження на AI-агента".`,  },
  {
    id: "hr-script-quality",
    title: "HR: оцінка якості скриптів менеджерів",
    area: "hr",
    description: "AI слухає дзвінки + читає IG — оцінює дотримання скриптів, tone of voice, response time. Топ-фрази що працюють",
    status: "partial",
    effort: "M",
    impact: 4,
    note: "AI-оцінка скриптів вже є, треба згрупувати по менеджерах + KPI dashboard",
    docRef: "HR block — Розділ 13",
    details: `Кожен менеджер отримує паспорт з KPI:
- % дотримання скриптів (вже є AI-оцінка)
- Sales conversion (запит → замовлення)
- Середній чек, AOV
- Response time (медіана + p95)
- NPS після спілкування (якщо є збір)
- Кількість ескалацій до власника
- Топ-5 фраз які цей менеджер вживає краще за середнє (lift)

Тижневий звіт: топ-перформер + кому потрібен тренінг + персональні поради.`,  },
  {
    id: "hr-tg-owner",
    title: "HR: Telegram-канал власника",
    area: "hr",
    description: "Щоденний звіт по команді: хто перевиконав, хто провалив, де треба тренінг, кого варто відзначити",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "HR block — Розділ 14",
    details: `Щоденний звіт у Telegram для власника:
- "За вчора: 47 розмов. Найкраще — Аліна (62% conversion, +12 vs середнє). Найгірше — Богдан (28%, треба тренінг)."
- Алерти: ескалації що чекають, скрипти що порушено
- Approval-кнопки: "Підвищити Аліну?", "Призначити тренінг Богдану?"

Weekly review (понеділок): зведена картина + рекомендовані рішення на тиждень.`,  },
  {
    id: "hr-tg-leader",
    title: "HR: Telegram-канал для HR/керівника",
    area: "hr",
    description: "Окремий контур для менеджера команди: хто на зміні, хто запізнюється, ескалації, навчання",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "HR block — Розділ 15",
    details: `Окремий канал для керівника команди / HR-людини:
- /shift — хто на зміні, хто запізнюється
- Ескалації що потребують уваги
- Тренінги: розіслати mini-quiz / FAQ-нагадування
- Журнал кейсів: коли AI втрутився, як менеджер відреагував

Не дублює канал власника — фокусується на operational level, без фінансових метрик.`,  },
  {
    id: "hr-onboarding-flow",
    title: "HR: онбординг нового співробітника",
    area: "hr",
    description: "Чек-лист першого дня / тижня / місяця. AI підказує власнику що показати/розповісти. Автоматичний знайомство з картки бізнесу",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "HR block — Розділи 8, 10",
    details: `Чек-лист онбордингу нового співробітника:
- Перший день: знайомство з картою команди, базовий доступ, читання правил бренду
- Перший тиждень: shadow-режим зі стандартними замовленнями
- Перший місяць: самостійна робота під наглядом AI (suggest mode)

AI-агент сам нагадує власнику: "У Анни 30-й день — пора підвищити автономію або залишити в shadow ще тиждень?". Базується на реальних KPI новачка.`,  },
  {
    id: "hr-training",
    title: "HR: модуль тренінгів",
    area: "hr",
    description: "На основі скриптів + актуальних правил Director — генерує мікро-уроки для менеджерів. Тренінг приходить в TG як 5-хв quiz",
    status: "backlog",
    effort: "L",
    impact: 3,
    docRef: "HR block — Розділ 13",
    details: `На основі поганих прикладів зі скриптів + актуальних правил Director — генеруються mini-уроки:
- 5-хв quiz у Telegram з 3-5 питаннями
- Гарні приклади vs погані (з реальних діалогів tenant-а, анонімізовано)
- Tone of voice / FAQ освіжач
- Нові правила що Director додав за тиждень

Метрика: % правильних відповідей на quiz, кореляція з покращенням реальних KPI менеджера.`,  },
  {
    id: "hr-modes",
    title: "HR: режими роботи (shadow / copilot / auto)",
    area: "hr",
    description: "Shadow — тільки фіксує. Copilot — пропонує тренінги/ескалації. Auto — сам розсилає тренінги на основі тригерів",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "HR block — Розділ 21",
    details: `Три режими роботи HR-агента:
- **Shadow** — лише фіксує. Збирає baseline без втручань. Перші 3-7 днів.
- **Copilot** — пропонує дії: "цьому треба тренінг", "цього варто похвалити". Власник погоджує.
- **Auto** — сам розсилає тренінги на основі тригерів (низький KPI, новий процес, помилка в скрипті).

Перемикач у кабінеті, на рівні агента або окремих сценаріїв.`,  },
  {
    id: "hr-director-questions",
    title: "HR: інтеграція з директором",
    area: "hr",
    description: "Director після HR ставить: «На цей тиждень виглядає що менеджер X дав 47% конверсії — підвищити чи дочекатись 2-го тижня?»",
    status: "backlog",
    effort: "S",
    impact: 3,
    docRef: "HR block — Розділ 12",
    details: `Після weekly review від HR, Director ставить власнику конкретні питання:

"Аліна цього тижня показала 62% sales conversion (vs 47% тиждень тому). Це 3-й тиждень росту. Підвищити мотивацію (бонус, статус 'старший менеджер'), чи дочекатись стабільної планки 4-5 тижнів?"

"Богдан 2-й тиждень <30% — це нижче порогу. Тренінг (1 тиждень), переведення на іншу зміну, чи серйозна розмова?"

Approval-flow з 4 кнопками.`,  },

  // ─── Recruiter (рекрутер) — повна серія (27 розділів) ──────────────────
  {
    id: "recruiter-vacancy-text",
    title: "Recruiter: AI-генерація тексту вакансії",
    area: "recruiter",
    description: "На основі бизнес-картки + ролі. 4-5 версій під різні платформи (Work.ua / Robota.ua / Telegram / Instagram)",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Рекрутер block — Розділ 9",
    details: `AI-генерація 4-5 версій тексту вакансії під різні платформи:
- Work.ua / Robota.ua — формальний, з повною структурою (обов'язки, вимоги, умови)
- Telegram-канали — розмовний, з emoji, до 500 символів
- Instagram-сторіс — дуже коротко, 1-2 речення + carousel-формат
- LinkedIn — професійний, англійською якщо роль міжнародна

Базується на business-card (бренд, культура), профілі ролі, сегменті ринку. Власник може редагувати або відразу публікувати.`,  },
  {
    id: "recruiter-cv-scoring",
    title: "Recruiter: AI-скоринг CV",
    area: "recruiter",
    description: "На вхід CV PDF/DOCX → парсимо досвід, навички, кваліфікацію → скор vs scoreкард. Топ-3 з обґрунтуванням",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Рекрутер block — Розділ 10",
    details: `CV PDF/DOCX парситься AI:
- Досвід (роки, релевантність ролі)
- Навички (matching з вимогами)
- Кваліфікація (освіта, сертифікати)
- "Червоні прапорці" (часта зміна роботи, gaps)
- Tone CV — впевнений / шаблонний / творчий

Скор vs scorecard ролі (0-100). Топ-3 з обґрунтуванням: "Чому саме цей кандидат — релевантний досвід у X, додаткова перевага Y, але ризик Z". Власник схвалює перехід на наступний етап одним кліком.`,  },
  {
    id: "recruiter-funnel-screen",
    title: "Recruiter: воронка кандидатів у web-кабінеті",
    area: "recruiter",
    description: "Заявка → CV-скоринг → телефон → текст-інтерв'ю → відео → фінал. Drag-and-drop між стадіями + AI коментарі",
    status: "backlog",
    effort: "L",
    impact: 3,
    docRef: "Рекрутер block — Розділ 17",
    details: `Канбан-дошка з 6 стадіями:
1. Заявка надійшла
2. CV-скоринг (AI-етап)
3. Телефонний скринінг (15 хв)
4. Текстове інтерв'ю (анкета з 5-7 питань)
5. Відео-інтерв'ю (Layer 2)
6. Фінал з власником

Drag-and-drop між стадіями. На кожному кандидаті: AI-коментар, скор, наступна дія, час у поточній стадії. Алерти на застряглих > N днів. Bulk-actions: відмовити всім нижче порогу.`,  },
  {
    id: "recruiter-structured-interview",
    title: "Recruiter: структуровані запитання для власника",
    area: "recruiter",
    description: "AI генерує 8-12 питань на структуроване інтерв'ю + scorecard для оцінки. Власник просто ставить 1-5 за кожне",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Рекрутер block — Розділ 13",
    details: `AI генерує 8-12 структурованих питань для фінального інтерв'ю + scorecard:

Категорії:
- Hard skills (3-4 питання, специфічні для ролі)
- Behavioral (STAR-метод, 2-3 питання про реальні кейси)
- Cultural fit (2-3 питання за tone of voice бренду)
- Motivation + growth (1-2 питання)

Власник просто ставить 1-5 за кожне + короткий коментар. AI агрегує у фінальний скор + порівнює зі скоркардом інших кандидатів. Зменшує упередженість.`,  },
  {
    id: "recruiter-tg-candidate",
    title: "Recruiter: Telegram-канал кандидата",
    area: "recruiter",
    description: "Кандидат отримує лінк, заповнює анкету, записує відео-інтерв'ю прямо у TG bot. Прозорий статус заявки",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Рекрутер block — Розділ 16",
    details: `Кандидат отримує лінк → відкриває Telegram-bot → анкета:
- Базові питання у форматі чату (1 за раз)
- Запис відео-інтерв'ю прямо в TG (5 коротких відео)
- Прозорий статус заявки: "Ваша CV на скоринг", "Запрошуємо на текст-інтерв'ю", "Чекаємо рішення власника"
- Автоматична відмова з конструктивним feedback (не "не підходите", а "зараз шукаємо більше досвіду в X")

Зменшує load на HR-людину — процес самообслуговування.`,  },
  {
    id: "recruiter-tg-owner",
    title: "Recruiter: Telegram-канал власника",
    area: "recruiter",
    description: "Щоденний звіт: «надійшло 12 нових заявок · 3 у топ-10% по скору · 2 пройшли відео-інтерв'ю». Кнопки погодити фінал",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Рекрутер block — Розділ 15",
    details: `Щоденний звіт у Telegram для власника:
"За вчора:
- 12 нових заявок
- 3 пройшли CV-скоринг (топ-10%)
- 2 завершили відео-інтерв'ю
- 1 чекає твого фінального рішення (Аня М., скор 87)

Кнопки: Подивитись Аню / Запланувати ще 3 фінали на пʼятницю / Все відкласти на тиждень."

Daily 9:00, без notifications якщо нічого критичного.`,  },
  {
    id: "recruiter-hr-handoff",
    title: "Recruiter ↔ HR — handoff після найму",
    area: "recruiter",
    description: "Після hire — автоматичне передавання у HR-агент: онбординг, перші 30 днів, очікувані KPI",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Рекрутер block — Розділ 20",
    details: `Після offer-а від кандидата і прийняття — automated handoff у HR-агент:
- Створюється профіль працівника у HR DB
- Очікувані KPI на 30/60/90 днів (з scorecard вакансії)
- Чек-лист онбордингу (з HR-агента)
- AI-агент сам нагадає власнику: "Аня працює 3 дні — час shadow-mode brief"
- Decision Journal фіксує: "наймали для X, перші 3 місяці очікувані Y"

Зменшує "втрату" нових працівників між найманням і реальною роботою.`,  },
  {
    id: "recruiter-modes",
    title: "Recruiter: режими (shadow / copilot / auto)",
    area: "recruiter",
    description: "Shadow — лише агрегує. Copilot — пропонує дії. Auto — сам відсіває явно нерелевантні CV (з прозорим лог-ом)",
    status: "backlog",
    effort: "M",
    impact: 3,
    docRef: "Рекрутер block — Розділ 18",
    details: `Три режими роботи рекрутера:
- **Shadow** — лише агрегує заявки, скорить, ранжує. Не діє.
- **Copilot** — пропонує наступні дії: "цьому відмовити", "цього на текст-інтерв'ю". Власник погоджує.
- **Auto** — сам відсіває явно нерелевантні CV (зі стрімким лог-ом причин відмови, можна оскаржити). Не виключає AI-помилки.

Прозорість: кожне auto-рішення документується для аудиту. Власник може ввімкнути auto тільки після 2-3 тижнів довіри.`,  },

  // ─── Director — додаткові пункти зі спеки ──────────────────────────────
  {
    id: "director-tg-owner-flow",
    title: "Director: повний Telegram-flow власника",
    area: "director",
    description: "Daily 9:00 ranking · Weekly Sun 19:00 reflection · approval requests з inline-keyboard · кнопки швидких дій",
    status: "partial",
    effort: "L",
    impact: 5,
    mvp: true,    note: "Daily/Weekly skill-и працюють. Треба inline-keyboard для approvals (зараз без кнопок) + кнопки швидких дій",
    docRef: "Director 2.0 — Розділи 19, 20",
    details: `Повний Telegram-flow для власника:

**Daily 9:00 ranking** — Morning Brief з 3-5 фокусами на день, кнопки швидких дій (Customer Journey / Approval queue / Аналітик / Скіли).

**Weekly Sun 19:00 reflection** — Weekly Report з KPI WoW, причинно-наслідковими висновками, фокусом наступного тижня.

**Approval requests** — кожен з inline-keyboard: погодити / відкласти / поясни деталі / заборонити для сценарію. Edit_message прибирає кнопки після дії.

**Кнопки швидких дій** на ranking message: ранковий звіт / тижневий / аномалії / скіли / навчити агента.`,  },
  {
    id: "director-card",
    title: "Director: картка директора (web)",
    area: "director",
    description: "Особистий профіль директора з KPI: швидкість запуску, % виконаних домовленостей, дисципліна, зміна загальної оцінки компанії",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Director 2.0 — Розділ 23",
    details: `Особистий профіль директора у /dashboard/director з KPI:
- Швидкість запуску (час від першого діалогу до першої дії)
- Частка виконаних домовленостей (% approve → done)
- Дисципліна системи (нагадування про повернення до планів)
- Кількість вимірюваних перемог за 14 днів
- Зміна загальної оцінки компанії (delta MTD)
- Заповненість картки бізнесу (%)
- Кількість активних агентів
- Зменшення ручної участі власника (год/тиждень)

Це не список tools, а оцінка "наскільки директор реально корисний".`,  },
  {
    id: "director-weekly-review-screen",
    title: "Director: Weekly Review screen",
    area: "director",
    description: "Окремий екран у /dashboard/director — що було за тиждень, що зроблено, що відкладено, фокус наступного тижня",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Director 2.0 — Розділ 21 (Блок 6)",
    details: `Окремий екран /dashboard/director/weekly:
- Що було за тиждень (chronologic timeline)
- Що зроблено (approved actions з ефектом)
- Що відкладено / відхилено (з причинами)
- Фокус наступного тижня (3-5 цілей)
- Прогрес OKR-ів
- Команда: топ-перформер, де треба втрутитись
- Бізнес: KPI WoW, ROAS, конверсії

Згенеровано Director-skill кожної неділі 19:00, читається власником у понеділок зранку.`,  },
  {
    id: "director-7-30-plan",
    title: "Director: план 7/30 днів",
    area: "director",
    description: "Структурований план з трьома горизонтами: сьогодні (1-2 дії) / тиждень (3-5 цілей) / місяць (1-2 стратегічні)",
    status: "planned",
    effort: "M",
    impact: 4,
    mvp: true,    docRef: "Director 2.0 — Розділ 21 (Блок 4)",
    details: `Структурований план з трьома горизонтами:

**Сьогодні** (1-2 дії) — конкретні задачі на 2-4 години роботи власника.

**Тиждень** (3-5 цілей) — фокус на 5-7 днів. Має критерії успіху.

**Місяць** (1-2 стратегічні) — великі рухи: запустити нового агента, виправити канал, найняти роль.

Director оновлює це автоматично після кожного approve-action і weekly review. Власник бачить "Що сьогодні? Що цього тижня? Що цього місяця?" одним екраном.`,  },

  // ─── Customer Journey — додаткові ─────────────────────────────────────
  {
    id: "cj-reference-values",
    title: "CJ: референтні значення по індустрії",
    area: "customer_journey",
    description: "Бенчмарки CR кожної стадії для e-commerce жіночого одягу UA. Видно «ти на 12% нижче середнього на стадії X»",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "CJ doc — Розділи 6 (референтні значення)",
    details: `Референтні значення (бенчмарки) по індустрії e-commerce жіночого одягу UA:
- Sessions → view_item: 60-75% (Romashka 65% — норма)
- view_item → add_to_cart: 7-12% (Romashka 5.8% — нижче норми)
- add_to_cart → begin_checkout: 50-65% (Romashka 57% — норма)
- begin_checkout → purchase: 60-75% (Romashka 58% — нижче)

CJ funnel показує "ти на 12% нижче середнього на add_to_cart — це 600 грн втраченого обороту/день при поточних об'ємах". Конкретні числа дають масштаб проблеми.`,  },
  {
    id: "cj-expense-tracking",
    title: "CJ: витратна частина по каналах",
    area: "customer_journey",
    description: "Окремий блок: маркетинг, ЗП, операційні витрати на канал. Дозволяє рахувати unit-economics на стадію",
    status: "planned",
    effort: "M",
    impact: 4,
    docRef: "CJ doc — Розділи 6, 7, 8, 9 (витратна частина)",
    details: `Окремий блок витрат на канал:
- Сайт: маркетинг (FB/Google) + хостинг + розробка
- IG: контент + реклама + час менеджера
- Офлайн: оренда + ЗП консультантів + утиліти
- Маркетплейси: комісії + просування

Дозволяє рахувати unit-economics НА СТАДІЮ:
- "FB-кліки коштують 3 грн/штук → перегляд товару = 5 грн → add_to_cart = 86 грн → замовлення = 270 грн"
- Якщо AOV 800 грн з маржею 40% → з кожного замовлення 320 грн прибуток − 270 витрат = 50 грн чистого

Вирішує "наскільки цей канал реально прибутковий".`,  },
  {
    id: "cj-ig-frozen-dialogues",
    title: "CJ: блок «Завислі діалоги» в IG",
    area: "customer_journey",
    description: "Показує діалоги без відповіді > N годин з прогнозом втрачених грошей. Інтегровано з Sales workspace",
    status: "partial",
    effort: "S",
    impact: 4,
    note: "В IG detail page часткове є, треба окремий KPI з фінансовим ефектом",
    docRef: "CJ doc — Розділ 8 (KPI завислого діалогу)",
    details: `Окремий KPI блок:
- Кількість завислих діалогів (без відповіді > N годин)
- Прогноз втрачених грошей (середній CR DM × AOV)
- Розподіл по часу: < 4 год / 4-24 год / > 24 год / > 7 днів

Інтеграція з Sales workspace — ці діалоги з'являються в Inbox/Queue з пріоритетом "high" + AI-підказкою "цей клієнт хотів X, час N днів — спробуй ось такий запит".

Ефект: повертаємо втрачені дилоги, а не просто визнаємо що 32% губиться.`,  },

  // ─── Site agent — додаткові ───────────────────────────────────────────
  {
    id: "site-event-quality",
    title: "Site: перевірка коректності GA4-подій",
    area: "site",
    description: "Чи всі обов'язкові події (view_item / add_to_cart / begin_checkout / purchase) надсилаються? Чи мають правильні параметри (item_id / value)?",
    status: "planned",
    effort: "M",
    impact: 4,
    docRef: "Сайт-агент block — Розділ 30 (event quality)",
    details: `Без коректних подій агент сайту дає «красиві але неправильні висновки». Треба перед усіма висновками перевіряти:

- view_item — є? чи має item_id?
- add_to_cart — є? чи має value? чи срабатывает на всіх PDP?
- begin_checkout — спрацьовує тільки на корзині, не на header-button?
- purchase — є? чи має transaction_id (унікальний на замовлення)?

Якщо щось зламано — agent BLOCKS висновки і пише власнику: «Воронка GA4 ненадійна — починай з фіксу подій».`,
  },
  {
    id: "site-effect-tracker",
    title: "Site: трекер ефекту після зроблених фіксів",
    area: "site",
    description: "Після того як власник позначив BP-X як done — система запам'ятовує baseline і через 7-14 днів показує реальний lift CR",
    status: "planned",
    effort: "M",
    impact: 4,
    docRef: "Сайт-агент block — Розділ 29",
    details: `Після позначки BP-X як done — система:
1. Зберігає baseline-snapshot ключових метрик за 7 днів ДО
2. Чекає 7-14 днів (залежить від impact-розміру)
3. Порівнює нові 7 днів з baseline
4. Обчислює: чи був заявлений ефект, на скільки відхилення, чи є побічні ефекти

Запис у DecisionLog: "BP-CARTW-02 закрито 2026-04-27. Очікувано +0.5 п.п. add_to_cart CR. Реально +0.7 п.п. — підтверджено. UAH-impact +12 350 грн/міс."

Це закриває петлю довіри: власник бачить що рекомендації агента ДІЙСНО дають заявлений ефект.`,  },

  // ─── Sales — додаткові ───────────────────────────────────────────────
  {
    id: "sales-owner-dashboard",
    title: "Sales: Sales Dashboard для власника",
    area: "sales",
    description: "Зведена картина: обсяг продажів, AOV, маржа, % виконання плану, топ-3 проблеми (відмови / повернення / нестандартні)",
    status: "partial",
    effort: "M",
    impact: 4,
    note: "/customer-journey overview покриває частково, треба окремий sales-only screen",
    docRef: "ВП block — Розділ 8.1",
    details: `Sales Dashboard для власника на /dashboard/sales:

**Top-row KPI cards** — обсяг продажів, AOV, маржа, % виконання плану.

**Funnel snapshot** — прийняті → закриті → відмови (з деталізацією причин).

**Top-3 проблеми** — найгостріше (відмови / повернення / нестандартні) з посиланнями на діалоги.

**Manager leaderboard** — топ-3 + кому треба тренінг.

Не дублює CJ overview, фокусується саме на sales-операційці.`,  },
  {
    id: "sales-followup-board",
    title: "Sales: Follow-up Board (не оплатив, не відповів, COD)",
    area: "sales",
    description: "Окрема дошка зі стадіями: не оплатив / не відповів / покинутий кошик / COD / передзамовлення / повернення. Drag-n-drop",
    status: "planned",
    effort: "L",
    impact: 4,
    docRef: "Розділ 12 (Sales Workspace · Вкладка 3)",
    details: `Дошка з 6 стадіями (Канбан):
- Не оплатив — нагадати оплатити
- Не відповів — реактивувати
- Покинутий кошик — знижка / нагадування
- Накладений платіж — підтвердити отримання
- Передзамовлення — повідомити про прихід
- Повернення / обмін — обробити

Drag-and-drop між стадіями. AI пропонує наступну дію + draft-повідомлення для кожного клієнта. Bulk-actions: "відправити нагадування всім у 'Не відповів' старіше 24 год".`,  },
  {
    id: "sales-script-rules",
    title: "Sales: тренінг агента + правила бренду",
    area: "sales",
    description: "Окремий екран Training: good replies, bad replies, tone of voice, FAQ, стоп-фрази. Кожен «поганий приклад» стає правилом",
    status: "partial",
    effort: "M",
    impact: 4,
    note: "Звичайна Director memory є. Треба окремий UI-екран з 8 секціями",
    docRef: "Розділ 12 (Sales Workspace · Вкладка 4 Training)",
    details: `Окремий screen Training з 8 секціями:
1. **Good replies** — приклади що працюють
2. **Bad replies** — приклади що НЕ працюють (з поясненням чому)
3. **Tone of voice** — голос бренду
4. **FAQ** — типові питання + еталонні відповіді
5. **Стоп-фрази** — заборонене
6. **Ескалації** — коли передавати власнику
7. **Нові правила** — щотижневі додавання
8. **Історія змін** — git-log правил

Кожен "поганий приклад" → правило → впливає на майбутні AI-відповіді.`,  },

  // ─── Analytics — додаткові ────────────────────────────────────────────
  {
    id: "analytics-narrative-summary",
    title: "Analytics: narrative summary дня/тижня",
    area: "analytics",
    description: "Історія цифр у тексті: «цього тижня замовлень 142 (+12%), AOV 3 280 (-3%), maturity ROAS на FB просів через...»",
    status: "partial",
    effort: "M",
    impact: 3,
    note: "Director Daily/Weekly briefs роблять це частково, треба окремий screen з історією наративів + причинно-наслідкові пояснення",
    docRef: "Аналітик block — Розділ 13.2",
    details: `Історія цифр у тексті, не у таблиці:
"Цього тижня замовлень 142 (+12%), AOV 3 280 грн (-3%). ROAS на FB просів через старт нової кампанії з нижчим CTR. Причина — нова креатив-стратегія не зайшла, треба повернутись до попереднього варіанту."

Не лише числа — а зв'язки. Director Daily/Weekly briefs частково роблять це, але треба окремий screen з історією наративів за період + drill-down на причини.`,  },
  {
    id: "analytics-anomaly-screen",
    title: "Analytics: окремий екран аномалій",
    area: "analytics",
    description: "Лог всіх детекцій з historical context. Sortable за severity, filterable за метрикою. Click → причинно-наслідковий аналіз",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 16",
    details: `Окремий екран /dashboard/analytics/anomalies:
- Лог всіх детекцій (з z-score history)
- Filter by metric / severity / status (active / resolved)
- Click → причинно-наслідковий аналіз (causal engine)
- Прив'язка до approval-ів які мали закрити аномалію

Розширює функціонал Director M4 — не лише "є аномалія", а "ось вся історія аномалій + чи закрили їх".`,  },
  {
    id: "analytics-decisions-journal",
    title: "Analytics: журнал рішень аналітика",
    area: "analytics",
    description: "Кожна гіпотеза + результат через 7 днів: 'AI припустив що IG провал від ціни — змінили — підтвердилось'",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 17",
    details: `Журнал гіпотез аналітика з результатом через 7 днів:
- "AI припустив що IG-провал від ціни — змінили — підтвердилось +15% CR"
- "AI припустив що FB просів через креатив — повернули старий — НЕ підтвердилось, причина була інша"

Точність прогнозів — окрема метрика аналітика. Власник через місяць бачить: "З 23 гіпотез аналітика 18 підтвердились (78%) — можна довіряти його висновкам більше".`,  },

  // ─── Approvals — додаткові ────────────────────────────────────────────
  {
    id: "approvals-types",
    title: "Approvals: типи з різним ризик-профілем",
    area: "approvals",
    description: "Класи: low-risk auto-approve, medium запит у TG, high — обовʼязкова сесія з власником. Class задається агентом + Director rules",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Director 2.0 — Розділ 20",
    details: `Класи approval-ів з різним ризик-профілем:
- **Low-risk** (auto-approve) — типові операції без власника. Лог-only.
- **Medium-risk** — Telegram-нагадування з inline-keyboard, 24h SLA.
- **High-risk** — обов'язкова сесія з власником, не виконується без явного approve.

Class задається агентом + правилами Director Brain. Власник може налаштовувати в /dashboard/director/settings: "FB-кампанії з бюджетом > 5к → high-risk завжди".`,  },

  // ─── Platform / Topbar / Sidebar / States ─────────────────────────────
  {
    id: "platform-topbar",
    title: "Platform: top bar (notifications + avatar + search)",
    area: "platform",
    description: "Горизонтальна панель: search across customers/orders, notifications bell з approval queue, profile menu",
    status: "partial",
    effort: "M",
    impact: 2,
    mvp: true,    note: "Базовий header є, треба global search + notification dropdown",
    docRef: "Головна сторінка doc — Розділ 9",
    details: `Горизонтальна верхня панель:
- **Search** — global, по customers / orders / messages / agents. Cmd+K shortcut.
- **Notifications** — bell з approval queue badge, dropdown зі списком pending.
- **Profile menu** — права частина, avatar + tenant switch + settings + logout.

Зараз базовий header є — треба додати global search + notification dropdown. Це окремий screen-level component, інтегрується через layout.`,  },
  {
    id: "platform-empty-states",
    title: "Platform: empty/partial/critical states",
    area: "platform",
    description: "Що показувати на головній якщо немає GA4 / є тільки 3 дні даних / зараз критична аномалія",
    status: "planned",
    effort: "M",
    impact: 3,
    mvp: true,    docRef: "Головна сторінка doc — Розділ 16 (Стани)",
    details: `Що показувати на головній за різних умов:
- **Empty State** — щойно зареєструвався, немає даних. Onboarding wizard з 3 кроками.
- **Partial Data State** — тільки 3 дні, baseline ще збирається. "Дані ще накопичуються" + skeleton-UI.
- **Critical State** — поточна аномалія. Червоний баннер з 1-shot summary і кнопкою "Деталі".

Без цих станів власник видить "0 замовлень" і думає що platform зламана. UX-критично.`,  },
  {
    id: "platform-score-logic",
    title: "Platform: логіка score компанії",
    area: "platform",
    description: "Алгоритм генерації загальної оцінки 0-100: 40% результат + 35% здоров'я + 15% шлях клієнта + 10% впровадження",
    status: "done",
    effort: "M",
    impact: 4,
    mvp: true,    doneIn: "Phase F (Customer Journey cockpit)",
    docRef: "Головна сторінка doc — Розділ 15 + Director 2.0 21",
    details: `Алгоритм генерації загальної оцінки 0-100:
- **40% Результат** — чистий прибуток vs план, маржа vs benchmark
- **35% Здоров'я** — операційна ефективність, відсутність аномалій, retention
- **15% Шлях клієнта** — CR на ключових стадіях, % завислих
- **10% Впровадження** — заповнення картки бізнесу, активні агенти, AI-coverage

Визначає колір ring-у на головній (зелений 90+ / жовтий 50-89 / червоний < 50). LLM-висновок Haiku дає текстовий опис ("Сильна маржа 42%, але впровадження AI-агентів повільне — варто прискорити Sales автоматизацію").`,  },
  {
    id: "platform-tg-home",
    title: "Platform: Telegram-версія головної",
    area: "platform",
    description: "/start у TG-боті — стисла версія головної: оцінка, висновок дня, 3 фокуси, кнопки до агентів",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Головна сторінка doc — Розділ 13",
    details: `/start у TG-боті — стисла версія головної:
- Загальна оцінка компанії з кольоровим бейджем
- Висновок дня (1 речення)
- 3 фокуси на сьогодні (clickable buttons)
- Кнопки до агентів: 📊 Аналітик / 💰 Sales / 🌐 Сайт

Не дублює web — просто quick-access. Власник у транспорті може за 30 секунд побачити стан і прийняти 1-2 рішення без необхідності відкривати ноут.`,  },

  // ─── Memory / Brain — додаткові ───────────────────────────────────────
  {
    id: "memory-brain-rules-ui",
    title: "Memory: Brain rules UI (CRUD)",
    area: "memory",
    description: "Окремий екран зі всіма правилами Director: додати / видалити / категорії / scope (агент / глобально). Зараз через Director chat",
    status: "partial",
    effort: "M",
    impact: 3,
    note: "Database + tools є. UI — частково в /dashboard/director-activity, треба повноцінний CRUD-screen",
    docRef: "Director Brain — Phase 09-09",
    details: `Окремий екран /dashboard/director/brain з CRUD-ом правил:
- Список усіх правил Director з filter (категорія / scope / created_at)
- Створення / редагування / видалення
- Scope: глобально для всіх агентів / лише цей агент / лише ця роль
- Категорії: tone / стоп-фрази / ескалації / data-rules / behavior

Зараз правила створюються через Director chat (add_rule tool) + переглядаються у /dashboard/director-activity. Треба окремий повний CRUD-screen з search і bulk-edit.`,  },

  // ─── Integrations — нові з doc'у ──────────────────────────────────────
  {
    id: "int-marketplaces",
    title: "Інтеграція: маркетплейси (Rozetka / Prom)",
    area: "integrations",
    description: "Замовлення з Rozetka/Prom → unified Customers + sales workspace. Окремий канал у Customer Journey",
    status: "backlog",
    effort: "L",
    impact: 4,
    docRef: "Логіка системи — Розділ 19 + ВП block",
    details: `Замовлення з Rozetka / Prom / OLX:
- Webhook на нове замовлення → unified Customers (через email/phone identifier graph)
- Sync товарів — статус наявності, ціни
- Окремий канал у Customer Journey з власною воронкою (заявка → підтвердження → доставка)
- Відмови + повернення трекаються окремо (ризик-профіль інакший)

Romashka для маркетплейсів зараз не інтегрована — це великий канал що губиться.`,  },
  {
    id: "int-1c",
    title: "Інтеграція: 1С / власна система обліку",
    area: "integrations",
    description: "Залишки + рух товарів для агента закупки. Без заміни системи власника — режим AI Orchestration Layer",
    status: "backlog",
    effort: "XL",
    impact: 4,
    docRef: "Логіка системи — Розділ 2 (Orchestration Layer)",
    details: `1С / власна система обліку як джерело даних для Buyer-агента:
- Залишки товарів (snapshot щодня)
- Рух товарів (закупки / продажі / повернення / списання)
- Постачальники + умови
- Категорії + структура асортименту

Без заміни системи власника — режим AI Orchestration Layer (читаємо, не пишемо). Інтеграція через REST API 1С / OData / прямий SQL до бази.

XL effort бо: специфіка 1С у кожного власника різна, треба конфігурабельний adapter.`,  },

  // ─── Schedules — додаткові ────────────────────────────────────────────
  {
    id: "schedule-buyer-morning",
    title: "Schedule: ранковий звіт закупки 09:00",
    area: "schedules",
    description: "Cron-skill що формує buyer morning brief через buyer-agent + надсилає у Telegram",
    status: "backlog",
    effort: "S",
    impact: 4,
    docRef: "Закупщик block — Розділ 14",
    details: `Cron-skill 09:00 щодня:
1. Викликає buyer-agent з prompt "сформуй ранковий звіт по запасах"
2. Buyer повертає structured-data: топ-маржа / ризик-нестачі / заморожені / новинки / 3 рекомендації
3. Скіл форматує у Telegram-message з кнопками швидких дій
4. Надсилає у головний чат власника

Без цього — buyer-агент є, але власник його не бачить регулярно. Скіл забезпечує proactive-режим.`,  },
  {
    id: "schedule-hr-weekly",
    title: "Schedule: HR weekly review понеділок",
    area: "schedules",
    description: "Cron-skill — топ менеджер, аутсайдер, ескалації, рекомендовані тренінги",
    status: "backlog",
    effort: "S",
    impact: 3,
    docRef: "HR block — Розділ 14",
    details: `Cron-skill понеділок 10:00:
1. Викликає HR-агент з prompt "тижневий звіт по команді"
2. HR повертає: топ-перформер, кому треба тренінг, ескалації, рекомендації
3. Forматується в weekly-report з approval-кнопками
4. Надсилається у Telegram + з'являється у /dashboard/hr/weekly

Тригерить регулярне HR-мислення власника, без необхідності щотижня самому згадувати "а як там команда?".`,  },

  // ─── Director: ще пункти зі спеки ─────────────────────────────────────
  {
    id: "director-handoff-protocol",
    title: "Director: handoff клієнта в перший агент",
    area: "director",
    description: "Director після онбордингу формує контекст-pack і передає у вузький агент: «ось бренд, ось болі, починай з X»",
    status: "partial",
    effort: "M",
    impact: 4,
    mvp: true,    note: "create_agent_team вже частково робить це",
    docRef: "Director 2.0 — Розділ 16",
    details: `Director після онбордингу формує context-pack і передає у вузький агент:
- Базова бізнес-інформація з картки бізнесу
- Виявлені болі та цілі
- Tone of voice + правила бренду
- Першу гіпотезу що цей агент має перевірити
- Очікувані KPI його успіху

Передача — не просто 'create_agent_team', а структурований brief. Без втрат. Агент стартує з контекстом і не ставить власнику ті ж самі питання які вже задавав Director.`,  },
  {
    id: "director-modes",
    title: "Director: режими роботи (фокус)",
    area: "director",
    description: "Швидко-гасити-пожежу / Паралельно / Послідовно — змінює пріоритети агентів і tone комунікацій",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Director 2.0 — Розділ 15",
    details: `Три фокус-режими директора:
- **Швидко-гасити-пожежу** — top-1 проблема, всі інші відкладені. Daily focus = виправити її.
- **Паралельно** — швидка користь + системне впровадження. 50/50 уваги.
- **Послідовно** — спочатку базис (картка, перший агент), потім розширення.

Режим змінює тон комунікацій (спокійний/тривожний), пріоритети approval-ів, частоту тривог. Власник перемикає у /dashboard/director/settings або через діалог: "переходимо в пожежний режим".`,  },
  {
    id: "director-rbac",
    title: "Director: доступи і безпека (RBAC)",
    area: "director",
    description: "Ролі: власник / директор з делегуванням / менеджер / спостерігач. Аудит-лог дій",
    status: "partial",
    effort: "M",
    impact: 3,
    note: "Better Auth + сесії є; формальної RBAC матриці на агенти/функції немає",
    docRef: "Director 2.0 — Розділ 28",
    details: `Роль-системи з матрицею:
- **Власник** — повний доступ
- **Директор з делегуванням** — частина дій (наприклад, погоджує approval до 5к грн без власника)
- **Менеджер** — лише свої клієнти + Sales workspace
- **Спостерігач** — read-only

Аудит-лог: кожна дія фіксується в DecisionLog з ким зробив, коли, яким чином (web/TG/auto). Власник бачить "за тиждень X дій від AI, Y дій від Аліни, Z від Богдана".`,  },
  {
    id: "director-customer-journey-integration",
    title: "Director: CJ як частина роботи директора",
    area: "director",
    description: "Director може посилатись на конкретні стадії воронки у chat: «провал на add_to_cart — починаємо звідти»",
    status: "partial",
    effort: "S",
    impact: 3,
    note: "Director має read-only доступ через site_funnel_cache, треба формалізувати посилання у відповідях",
    docRef: "Director 2.0 — Розділ 26",
    details: `Director може посилатись на конкретні стадії воронки прямо у chat:
"Бачу провал на add_to_cart — 5.8% (норма 8-12%). Починаємо звідти. Передаю агенту сайту з інструкцією: focus на check-out btn placement + product image quality."

Read-only доступ до site_funnel_cache. Director не змінює дані воронки, лише розмовляє ними. Може формувати approval-и з прив'язкою до стадії: "Зміни X для add_to_cart очікуються +0.7 п.п.".`,  },
  {
    id: "director-analyst-interaction",
    title: "Director ↔ Аналітик interaction",
    area: "director",
    description: "Director питає аналітика «чому впав ROAS?» — отримує текст пояснення з числами. Не дублює аналітика, делегує",
    status: "partial",
    effort: "M",
    impact: 3,
    note: "ask_specialist частково. Треба чіткий протокол analyst-output",
    docRef: "Director 2.0 — Розділ 27",
    details: `Director не дублює аналітика. Він питає його через ask_specialist:

"чому впав ROAS на FB?" → ask_specialist(role: 'analytics', question: '...')

Аналітик повертає текст пояснення з числами (Phase 15 PES). Director читає, синтезує у управлінську дію: "ROAS впав на 23% через старт нової кампанії з 3× нижчим CTR. Рекомендую — поставити цю кампанію на pause, перевести бюджет на стабільну. Очікуваний ефект — повернення ROAS до baseline через 2 дні. Погоджуєш?".`,  },
  {
    id: "director-business-card-protocol",
    title: "Director: read/write протокол картки бізнесу",
    area: "director",
    description: "Director під час сесій ОНОВЛЮЄ картку бізнесу (через write_memory tools). Картка не редагується вручну",
    status: "partial",
    effort: "M",
    impact: 4,
    mvp: true,    note: "memory tools є. Треба тригери update після кожної мікросесії і UI що показує diff",
    docRef: "Director 2.0 — Розділ 25",
    details: `Director під час сесій ОНОВЛЮЄ картку бізнесу через write_memory:
- Кожна нова відповідь власника → запис у відповідну вкладку
- Зміни tone of voice → правила бренду
- Нові процеси → вкладка "процеси"

Картка не редагується вручну власником у звичайному flow — лише через діалог з директором. UI показує diff: "Оновлено вкладку 'Канали': додано Instagram з відсотком 35% обороту".

Версійність — кожне оновлення створює знімок (rollback-able).`,  },

  // ─── Site agent: окремі екрани (block ~lines 6705-6815) ──────────────
  {
    id: "site-clarity-tab",
    title: "Site: Вкладка «Clarity» — поведінкові сигнали → задачі",
    area: "site",
    description:
      "Оцінка сайту за поведінкою (score /100) + таблиця UX-сигналів (rage/dead clicks, quick backs) з фільтрами, впевненістю та впливом. Числа з Clarity API, тексти — AI",
    status: "done",
    effort: "L",
    impact: 4,
    mvp: true,
    doneIn: "clarity-tab (2026-07-02)",
    note: "services/clarity-signals.ts: скан 2-3 NL-запити (totals + page×device×source, top 30) раз на добу, кеш 24г у tenant_meta. Score детермінований (рейти на сесію: quick backs/rage/dead/JS/scrolls, формула в коді); LLM (cheap tier) лише назви проблем + confidence/impact + верхній блок (status/що бачимо/чому важливо/порада), заборона вигадувати елементи й числа, fallback-тайтли при відмові LLM. КВОТА Clarity 10 запитів/день: добовий бюджет вкладки 6 (лічильник clarity_signals_calls_<date>), кулдаун форс-оновлення 30 хв, stale-кеш до 7 днів як fallback. UI: донат-score + 3 пояснювальні блоки + 5 summary-карток (сесії/проблеми/критичні/найбільший ризик/найчастіший сигнал) + 4 фільтри + таблиця з пагінацією + «Останні докази» (heatmap/recordings deep-links через tenant_meta clarity_project_id, інлайн-інпут) + кнопка «Відкрити в Microsoft Clarity». Verified: live скан Romashka (43,5к сесій, score 61, 20 сигналів, 3-dim розбивка з джерелами ig/organic) + Playwright скрін. DEEP-LINKS (07-02 v3): «Відкрити» веде на ТОЧНУ проблему в Clarity — реверс-інженерений формат URL (верифіковано на живому demo-проєкті Clarity): impressions?URL=2;6;^regex$ (записи по сторінці), heatmaps?heatmapDeviceType=0|1|2(Mobile/Tablet/PC)&heatmapType=0|1|3|4(Click/Scroll/Dead/Rage)&URL=2;6;^regex$ (карта конкретного типу/пристрою/сторінки). rage/dead/scroll-сигнали → теплокарта, quick backs/JS → відфільтровані записи. Regex-формат Clarity: ^escaped(\?.*)?$. ID проєкту Romashka w1ri5ubl76 (з трекінг-тега сайту) записано в staging tenant_meta.",
  },
  {
    id: "site-priorities-tab",
    title: "Site: Вкладка «Пріоритети» — обʼєднані проблеми всіх джерел + що робити першим",
    area: "site",
    description:
      "Управлінський екран: проблеми з Clarity + GA4 + HTML-аудиту в одному ранжованому списку. Пріоритет = (вплив на прибуток × впевненість) / складність, формула показана власнику",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,
    doneIn: "site-priorities tab (2026-07-03)",
    note: "services/site-priorities.ts + /api/dashboard/site/priorities (кеш 1г; читає ЛИШЕ кеші джерел через їх getter-и — сам зовнішні API не смикає). Нормалізація: Clarity signals (₴-оцінка = events/міс × CR × recovery 0.5/0.25/0.1 × AOV, консервативно, «оцінка не гарантія»; AOV/CR додані в ga-signals payload), GA problems (₴/тиж × 4.33), SEO-аудит checks (critical/warning → критичні/контентні/покращення). КРОС-МАТЧИНГ: Clarity-сторінка ↔ GA-етап (stageOfPath: catalog/product/checkout) + merge → «Підтверджено 2 джерелами», confidence high, ₴ з GA. Типи: критичні/UX/контентні/аналітичні/покращення (детерміновано). Пріоритет-скор = (impactUah|сурогат типу × confW 1/.7/.4) / diffW 1/1.6/2.5 → P1-P3. «Потенційний прибуток» НЕ рахує GA-рядки сегмента «Всі» (дублюють суму сегментів). Резерв слотів: до 4 аналітичних + 6 аудиту (інакше ₴-скоринг їх витісняє). LLM (cheap, purpose site_priorities) лише верхній блок: statusLine/висновок/3 аргументи джерел/порада, БЕЗ сумарних грошей. UI: донат-score (середнє 3 джерел) + 4 стат-картки (проблеми з розбивкою по типах, критичні з кнопкою-фільтром, ≈₴/міс «оцінка не гарантія», джерела даних + сторінок перевірено) + 5 фільтрів + таблиця 11 колонок (джерела-бейджі, чому пріоритет) + футер формули. «Відкрити» перемикає на вкладку-джерело. ОБʼЄДНАНО З «ГОЛОВНОЮ» (07-03 v2, кеш site_priorities_cache_v2): вкладки «Пріоритети» нема — воронка зверху «Головної», під нею пріоритетний блок. Рядок таблиці розгортається у 4 квадранти «Що / Чому / Як виправити / Очікуваний результат» (howToFix+expectedResult — детерміновані плейбуки по джерелу/етапу/типу) + кнопки «Створити ТЗ» (TechSpecModal з ProblemContext) і «Відкрити джерело». З «Головної» прибрані SiteAgentCard і ClarityBlock-ROI (prefetch clarity-insights вимкнено — квота), TechReadiness → нагору SEO + AI. Виправлені баги: session-guard у buildTracking ховав Unassigned-артефакти (знайдено 4 діри трекінгу). site-seo-audit.ts: прибрано import server-only (tsx gotcha §13).",
  },
  {
    id: "site-ga-tab",
    title: "Site: Вкладка «Google Analytics» — просідання, аномалії, трекінг → задачі",
    area: "site",
    description:
      "Оцінка воронки за GA4 (score /100) + таблиця просідань конверсії по сегментах (пристрій×канал) проти еталону (минулі 7 днів), z-score аномалії CR/доходу, детерміновані проблеми трекінгу подій. ₴-вплив на тиждень",
    status: "done",
    effort: "L",
    impact: 5,
    mvp: true,
    doneIn: "ga-signals tab (2026-07-02)",
    note: "services/ga-signals.ts + /api/dashboard/site/ga-signals (кеш 6г, ?refresh=1). Метод: 3 GA4-звіти через новий runGa4RawReport (2 dateRanges → синтетичний вимір date_range_0/1; eventName inList view_item/add_to_cart/begin_checkout/purchase; daily 28д). Воронка: Каталог→Картка→Кошик→Оформлення→Покупка по сегментах device×sessionDefaultChannelGroup. Числа лише з коду: CR клампиться ≤100% (інакше мікро-знаменники давали -17233 п.п.), мін. база етапу 20, мін. сегмент 30 сесій; ₴-вплив = |Δ|×база×AOV ±30%; score = 100 − топ-4 просідань − 6×трекінг − 4×аномалії. Трекінг-чеки: view_item без add_to_cart; transactions без begin_checkout (<30%); purchase events vs transactions ×1.25/×0.8. Аномалії: z-score CR і доходу за день проти 21д базлайну, |z|≥2, картки зі спарклайнами (14д). LLM (cheap) лише назви+confidence+верхній блок (status/бачимо/ризик/порада), fallback-тайтли. UI: донат-score (reuse CsScoreDonut) + 5 карток (drop-off/сегмент/відхилення/аномалії/трекінг) + 4 фільтри + таблиця P1-P3 з ₴/тиж + картки аномалій + «Відкрити в GA» (analytics.google.com/#/p<propertyId>). Verified live Romashka: -17.22 п.п. Кошик→Оформлення, z=-3.2 дохід 26.06, трекінг-діра Mobile·Unassigned (206 tx / 3 begin_checkout). Вкладка «Аналітика» стане deprecated — приберемо після приймання. АУДИТ ЦИФР v2 (07-02 PM, кеш-ключ ga_signals_cache_v2): (1) ₴-вплив тепер множиться на P(purchase|entity воронки) — верхні етапи були завищені ~20-50× (Tablet·OrgSocial ₴58-108к → ₴318-591); (2) ранжування просідань за ₴ (не |Δ|×√base) — грошові попереду, мікро-сегменти вниз; (3) «Найгірший сегмент» лише якщо реально впав (else «—»); (4) «Відхилення від еталону» = МЕДІАНА просідань (типове); (5) аномалії = унікальні ДНІ; (6) назви LLM без цифр + трекінг-назви детерміновані (LLM їх ковервкав); (7) трекінг-рядки завжди в списку (5 слотів, P3). Блок аномалій зрозумілий: зелений коридор норми (mean±2σ), пунктир середнього, ЧЕРВОНА точка аномального дня, легенда «норма X–Y», людський текст «26.06 конверсія впала до 0.58% — за межами коридору 0.70–1.27%». + перенесені 3 міні-чарти переходів (Перегляд→Кошик/Кошик→Оформлення/Оформлення→Покупка, SiteFunnelMini reuse, дані funnelDaily 28д з 4-го GA4-звіту date×eventName).",
  },
  {
    id: "site-screen-problems",
    title: "Site: Екран «Проблеми»",
    area: "site",
    description: "Список усіх знайдених проблем сайту з фільтрами по стадії воронки. Кожна — клік → деталі + докази",
    status: "partial",
    effort: "M",
    impact: 4,
    note: "SiteAgentCard показує найкритичнішу — треба окремий screen зі всіма",
    docRef: "Сайт-агент block — Розділ 16",
    details: `Окремий екран /dashboard/site/problems зі списком всіх знайдених проблем:
- Фільтри: стадія воронки / impact / status (open/in-progress/done)
- Сортування: за expected UAH-impact, за CR-провалом, за давністю
- Кожен рядок: проблема, докази (link), очікуваний effect, статус
- Bulk-actions: створити TZ для всіх top-5

Зараз SiteAgentCard показує лише найкритичнішу — потрібен повноекранний view коли власник хоче "пройтись по всьому".`,  },
  {
    id: "site-screen-evidence",
    title: "Site: Екран «Докази»",
    area: "site",
    description: "Сирі дані за вибраною проблемою: GA4 events, Clarity recordings, screenshot, метрики до/після",
    status: "partial",
    effort: "M",
    impact: 3,
    note: "Toggle «Відкрити докази» на SiteAgentCard. Треба окремий повний-екран view",
    docRef: "Сайт-агент block — Розділ 17",
    details: `Сирі джерела за вибраною проблемою на повноекранному view:
- GA4 events (raw count + breakdown)
- Clarity recordings (link на конкретні сесії)
- Screenshot ключових сторінок (auto-capture через PSI або custom Lighthouse)
- Метрики до/після (якщо проблема з історією)
- Коментарі від інших агентів (analytics confirmation)

Toggle "Відкрити докази" на SiteAgentCard зараз показує мінімум — окремий screen дає повний context для розробника / підрядника.`,  },
  {
    id: "site-screen-tz",
    title: "Site: Екран «Технічне завдання»",
    area: "site",
    description: "Згенероване ТЗ для розробника / підрядника: проблема + критерії приймання + UAH-вплив + спосіб перевірки",
    status: "partial",
    effort: "M",
    impact: 4,
    note: "approval_request створюється. Треба окремий screen з усіма ТЗ + статус виконання",
    docRef: "Сайт-агент block — Розділ 18",
    details: `Згенеровані ТЗ для розробника / підрядника:
- Title (короткий, action-oriented)
- Опис проблеми (з SiteAgentCard)
- Критерії приймання (об'єктивні: "step CR add_to_cart > 7% за 7 днів")
- Спосіб перевірки (як reviewer відкриє метрики)
- Очікуваний UAH/міс ефект (з recoveryUah)
- Прив'язка до BP-ID з каталогу best practices

Експорт у Markdown / PDF / Linear / Notion. Approval flow підключається тут — після виконання система чекає 7 днів і фіксує реальний lift.`,  },
  {
    id: "site-screen-hypotheses",
    title: "Site: Екран «Гіпотези»",
    area: "site",
    description: "Список гіпотез які агент перевіряє. Кожна — вхідні параметри + очікуваний effect + актуальний стан",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Сайт-агент block — Розділ 19",
    details: `Список гіпотез які агент перевіряє у фоні:
- "Якщо змінити X, очікую +Y п.п. CR на add_to_cart"
- Поточний стан: тестується / прийнято / відкинуто
- Метод перевірки: A/B-test / before-after / cohort

Кожна гіпотеза зв'язана з конкретною проблемою або BP-ID. Агент сам оновлює статус коли є нові дані. Власник може ініціювати власну гіпотезу через UI: "Перевір що буде якщо знизити ціни доставки на 20%".`,  },
  {
    id: "site-screen-effect-log",
    title: "Site: Екран «Журнал ефекту»",
    area: "site",
    description: "Хронологія: проблема знайдена → ТЗ створено → виконано → ефект зафіксовано. Зведений UAH-impact",
    status: "planned",
    effort: "M",
    impact: 4,
    docRef: "Сайт-агент block — Розділ 20",
    details: `Хронологічний журнал site-агента:
- Проблема знайдена (дата, автоматично)
- ТЗ створено (дата, ким)
- Виконано (дата, ким, з посиланням на commit/PR/release)
- Ефект зафіксовано (дата, реальний lift vs очікуваний)

Зведений UAH-impact: за квартал агент допоміг покращити сайт на +X грн/міс. Це — головна метрика довіри. Без цього важко довести цінність агента власнику.`,  },
  {
    id: "site-tg-comms",
    title: "Site: Telegram-комунікація",
    area: "site",
    description: "Daily morning push з топ-проблемою + кнопки. Anomaly alerts при різких CR-провалах",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Сайт-агент block — Розділ 21",
    details: `Telegram-комунікація site-агента:
- Daily 9:00 push з топ-проблемою + кнопками "Створити ТЗ" / "Деталі" / "Відкласти"
- Anomaly alert при різких CR-провалах (>20% від baseline)
- Weekly digest по неділях: "За тиждень закрито X проблем, lift +Y грн/міс"
- На вимогу: "Покажи проблеми /dashboard/site"

Не спамить — мовчить якщо все стабільно (anomaly_only mode).`,  },
  {
    id: "site-anomalies",
    title: "Site: автодетект аномалій сайту",
    area: "site",
    description: "Раптовий 4xx/5xx стрибок · падіння CR на стадії · збільшення JS errors · падіння трафіку. → Telegram alert",
    status: "planned",
    effort: "M",
    impact: 4,
    docRef: "Сайт-агент block — Розділ 24",
    details: `Site-агент детектить:
- Раптовий стрибок 4xx/5xx у GA4 events
- Падіння CR на ключовій стадії > 30% (z-score)
- Збільшення JS errors у Clarity > 50%
- Падіння трафіку > 25% (з контекстом FB ads / SEO)
- Поломка форм (drop у submit events)

→ Telegram alert у власника з 1-shot-summary і кнопкою "Відкрити деталі". Інтегровано з з-агентом аналітики для cross-check.`,  },

  // ─── Analytics: ще ─────────────────────────────────────────────────────
  {
    id: "analytics-three-roles",
    title: "Analytics: 3 ролі аналітика",
    area: "analytics",
    description: "Спостерігач (читає метрики) / Пояснювач (causal) / Гіпотезіст (генерує гіпотези). Перемикач у UI",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 6",
    details: `Перемикач "як думає аналітик" у UI:
- **Спостерігач** — лише читає метрики, без висновків. "Ось числа."
- **Пояснювач** — причинно-наслідковий. "Числа впали через X, бо Y."
- **Гіпотезіст** — генерує гіпотези на майбутнє. "Якщо зробити X, очікую Y."

Кожна роль має свій prompt + tool-set. Спостерігач — найдешевший, гіпотезіст — найдорожчий (більше context, більше LLM-calls). Власник обирає за потребою.`,  },
  {
    id: "analytics-tg-comms",
    title: "Analytics: Telegram-комунікація",
    area: "analytics",
    description: "Питання у природній мові: «який ROAS останніх 7 днів» → отримуєш числа з графіком + LLM-пояснення",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 14",
    details: `Природна мова у Telegram:
- "який ROAS останніх 7 днів?" → числа з графіком + LLM-пояснення
- "порівняй з минулим місяцем" → comparison + delta-bars
- "чому падає AOV?" → causal-аналіз + 2-3 гіпотези
- "топ-3 проблеми сьогодні" → ranked list

Голосові — той самий flow, через Whisper transcription. Bot пам'ятає контекст останніх 5 запитів у сесії.`,  },
  {
    id: "analytics-data-quality",
    title: "Analytics: достовірність даних",
    area: "analytics",
    description: "Перед висновками — перевірка чи дані повні (всі канали, всі дні, відсутність gaps). Інакше «дані ненадійні» badge",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 9",
    details: `Data-quality gate перед висновками:
- Чи всі канали підключені (GA4, FB, IG, CRM)?
- Чи дані за останні 7 днів повні (немає gaps > 2 годин)?
- Чи метрики у звичних діапазонах (не корумповані)?

Якщо щось не так → аналітик повертає "⚠️ Дані ненадійні: GA4 не отримує події з вчора 18:00. Перш ніж робити висновки, перевір tracking. Альтернативно, можу працювати з частковими даними з помітками."

Краще "не знаю" ніж впевнено помилитись.`,  },
  {
    id: "analytics-launch-via-director",
    title: "Analytics: запуск аналітика через директора",
    area: "analytics",
    description: "Director у chat може делегувати: «передай аналітику питання по ROAS» → ask_specialist(analytics, ...)",
    status: "done",
    effort: "S",
    impact: 3,
    mvp: true,    doneIn: "ask_specialist tool",
    docRef: "Аналітик block — Розділ 11",
    details: `Director у chat може делегувати:
"Передай аналітику питання: чому впав ROAS на FB?" → ask_specialist(role: 'analytics', question: '...')

Аналітик повертає структурований відповідь з причинами. Director не дублює його, а синтезує висновок у управлінську дію. Зараз працює через ask_specialist tool — Phase 15 PES забезпечує структуру.`,  },

  // ─── Sales v2: окремі модулі ──────────────────────────────────────────
  {
    id: "sales-onboarding-module",
    title: "Sales: модуль онбордингу (4 блоки)",
    area: "sales",
    description: "Швидка кваліфікація + структура відділу + бізнес-процеси + tone of voice. 30-45 хв з власником",
    status: "partial",
    effort: "L",
    impact: 4,
    mvp: true,    note: "Базовий onboarding є. Треба формалізувати у 4 блоки + scripts",
    docRef: "ВП block — Розділ 6",
    details: `4 блоки онбордингу sales-агента (30-45 хв з власником):

**1. Швидка кваліфікація (5 хв)** — розмір команди, канали, об'єм.
**2. Структура відділу (10-15 хв)** — ролі, KPI, hand-off, ескалації.
**3. Бізнес-процеси (10-15 хв)** — типове замовлення / нестандартне / винятки.
**4. Tone of voice (5-10 хв)** — як говорити, які стоп-фрази.

Виходи: контекст-pack для агента + draft-картка бізнесу + перші правила Brain. Без цього — агент пише generic-тексти.`,  },
  {
    id: "sales-knowledge-module",
    title: "Sales: модуль прийому знань",
    area: "sales",
    description: "Власник скидає файли (Excel прайс, PDF політики, screenshot правил) → AI парсить у структуровані знання",
    status: "partial",
    effort: "L",
    impact: 4,
    note: "knowledge_files migration є + RAG. Треба UI для drag-n-drop + auto-parse",
    docRef: "ВП v2 — Розділ 16.2",
    details: `Власник скидає файли через UI (drag-n-drop):
- Excel прайс — парситься у products table
- PDF політики — у knowledge_files з RAG
- Screenshots правил — OCR + structured extract
- Word docs процесів — split у markdown chunks

AI парсить у структуровані знання, показує preview, власник підтверджує або виправляє. Зменшує час налаштування агента з тижня до години.`,  },
  {
    id: "sales-process-builder",
    title: "Sales: модуль побудови процесів",
    area: "sales",
    description: "AI з власником будує карту: типове замовлення → нестандартне → винятки. Кожен процес — flowchart + правила",
    status: "planned",
    effort: "L",
    impact: 4,
    docRef: "ВП v2 — Розділ 16.3",
    details: `AI з власником будує карту процесів через діалог:
"Покажи на одному прикладі — як зараз виглядає типове замовлення?"
"А коли клієнт хоче вернути — як обробляємо?"
"А що з нестандартним розміром — на склад звертаєшся?"

Кожен процес — flowchart (ноди = події, edge = умови) + правила (if-then). Зберігається у DB як executable workflow. AI потім слідує цьому flow при автоматичній обробці замовлень.`,  },
  {
    id: "sales-task-module",
    title: "Sales: модуль задач (queue + assignments)",
    area: "sales",
    description: "Інтегрований task system — задачі з усіх агентів збираються тут. Призначення менеджеру / самовиконання",
    status: "partial",
    effort: "M",
    impact: 4,
    note: "approval_requests + tasks tables частково. Треба unified view + assignments",
    docRef: "ВП v2 — Розділ 16.7",
    details: `Unified task queue з усіх агентів:
- Site-агент створив "Виправити add_to_cart на mobile"
- Buyer-агент створив "Замовити 30 шт від Постачальника А"
- HR створив "Тренінг для Богдана"
- Sales створив "Пере-зв'язатись з Іваном по покинутому кошику"

Призначення: менеджер / підрядник / AI-самовиконання. Прогрес-трекер. Інтегровано з approvals — кожна задача може потребувати approve власника.`,  },
  {
    id: "sales-shadow-mode",
    title: "Sales: тіньовий режим (дні 1-3)",
    area: "sales",
    description: "AI спостерігає роботу менеджерів без втручань. Збирає baseline: response time, conversion, скрипти. Після 3 днів — звіт",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "ВП v2 — Розділ 1.7",
    details: `Перші 1-3 дні роботи sales-агента:
- AI спостерігає роботу менеджерів-людей без втручань
- Збирає baseline: response time, conversion, які скрипти спрацьовують
- Через 3 дні видає звіт: "За 3 дні 47 розмов, конверсія 32%, AVG response time 18 хв. Топ-3 виявлених проблеми: A, B, C."

Без цього — агент не знає baseline і не може довести цінність власнику. Власник у будь-який момент бачить що саме AI 'слухає' і чого вчиться.`,  },
  {
    id: "sales-copilot-mode",
    title: "Sales: режим співпілота (дні 4-7)",
    area: "sales",
    description: "AI готує draft-відповіді, менеджер підтверджує. Метрика: % drafts прийнято без правок",
    status: "partial",
    effort: "M",
    impact: 4,
    note: "IG suggest mode = саме це. Треба те саме для сайту/Telegram",
    docRef: "ВП v2 — Розділ 1.8",
    details: `Дні 4-7: AI готує draft-відповіді для менеджера.

Менеджер бачить пропозицію перш ніж відправити, може:
- Надіслати як є (1 клік)
- Редагувати → надіслати
- Відхилити → AI вчиться на негативному прикладі

Метрика: % drafts прийняті без правок (target > 70% до кінця тижня). Якщо нижче — продовжуємо тренінг, не переходимо на Auto.

IG suggest mode = саме це для IG. Треба перенести на сайт + Telegram.`,  },
  {
    id: "sales-ig-feedback-fixes-tetiana",
    title: "IG Manager: фікси з тесту керівника відділу продажів",
    area: "sales",
    description: "Ескалації (дата поставки, магазин, мульти-пости), пам'ять обговорених товарів, чесність по замірах, бронь при запереченнях, фікс IBAN-тригера, автосинк фіда на проді",
    status: "done",
    effort: "M",
    impact: 4,
    doneIn: "2026-07-03 (quick-260703-i0e)",
    details: `Фідбек-раунд після тесту @t.arzamastseva (10 пунктів у Google Sheet):
- Нові детерміновані тригери ескалації: питання про дату поставки/наявність у майбутньому, візит/резерв у фізичному магазині, мульти-товарні пости без конкретики
- P0-C заміщення кандидатів тепер архівує (ai_displaced) — «і футболку» через 30 хв резолвиться у раніше обговорену модель
- Правила промпту: не казати «фото не прикріпилось» коли товар вже розпізнано; чисті посилання без ?variation_id; чесність по замірах (без «тканина тягнеться, підійде»)
- Плейбук §8: опрацювання заперечень з пропозицією броні за передплату 150 грн (оновлено system_prompt на staging + прод агентах)
- IBAN-автовідправка більше не тригериться на анонс «перш ніж надіслати реквізити» в апсейлі
- Інфра: на проді не було crontab для run-skills — фід не синкався з 24.06 (звідси невірна наявність). Додано cron, каталог оновлюється щогодини`,  },
  {
    id: "sales-ig-photo-color",
    title: "IG Manager: розпізнавання кольору товару з фото клієнта",
    area: "sales",
    description: "Vision визначає колір на скріні; коли модельний ряд має кілька кольорів — ШІ веде розмову з кольору з фото, лінк/варіант вказують на нього",
    status: "done",
    effort: "S",
    impact: 3,
    doneIn: "2026-07-13",
    details: `Запит Тетяни після 2-го тесту: клієнт кидає фото білої сукні, а модельний ряд має декілька кольорів — ШІ показував усі кольори без урахування фото.

Тепер: vision-екстракція повертає ще й колір (укр. назвами бренду); при матчі модельного ряду варіант пінится до кольорової картки з фото (посилання/фото товару відповідають побаченому), а в блок «ОБРАНІ ТОВАРИ» додається рядок «📸 колір з фото: X» — ШІ говорить насамперед про цей колір, інші згадує як альтернативу. Явний вибір клієнта (chosen_color) має пріоритет. Без міграції (маркер у note).

Раунд 2 багфіксів (13.07, з того ж тесту): правила коригування замовлення (визначай який пункт правиться, не вигадуй колір/розмір), заборона «уточню і відпишу пізніше», заборона підтверджувати характеристики зі слів клієнта; скрейп складу тканини зі сторінки товару (mig 0050) + «Склад тканини» у промпт-блоці — «чи тягнеться?» тепер відповідається з даних.`,  },
  {
    id: "sales-ig-dialog-memory",
    title: "IG Manager: пам'ять історії діалогу з клієнтом",
    area: "sales",
    description: "ШІ пам'ятає весь діалог: тиждень історії у промпті + вічна пам'ять фактів (параметри, вибрані кольори/розміри, контакти, домовленості) зі старіших повідомлень",
    status: "done",
    effort: "M",
    impact: 4,
    doneIn: "2026-07-16",
    details: `Баг з тесту 16.07: клієнтка дала параметри 96/73/100 на початку розмови, а через 25 повідомлень ШІ відповів «не бачу ваших параметрів» — у промпт потрапляли лише останні 12 повідомлень.

Тепер:
- Вікно історії: до 40 повідомлень за останні 7 днів (з бюджетом символів; мінімум 12 завжди)
- Персистентна «пам'ять діалогу» (dm_thread_memory, mig 0051): повідомлення, що випадають з вікна, зливаються дешевою моделлю у стислий конспект фактів — параметри тіла, зріст, контакти й доставка, які кольори/розміри клієнт обрав чи відхилив, домовленості й обіцянки
- Пам'ять живе вічно (не обмежена тижнем) та інжектиться у промпт блоком «🧠 ПАМ'ЯТЬ ДІАЛОГУ»
- Злиття ліниве — один виклик лише коли щось нове випало з вікна
- Попутно: фікс — Sonnet 5 був відсутній у allowlist generate-роуту, прод тихо відповідав Haiku

Багфікси з того ж тесту (16.07):
- Колір замовлення: вибір клієнта словами («ну давайте блакитний») тепер має пріоритет над кольорами, які ШІ перелічив як альтернативи (раніше підсумок міг збрехати «коричневий»)
- Ескалація бачить УСІ повідомлення клієнта після останньої відповіді ШІ, а не лише останнє («повернення?» + «Так» поспіль більше не губиться)
- «Передаю менеджеру» / «менеджер підкаже» у згенерованій відповіді БЕЗ реальної ескалації → тепер автоматично ставить паузу і пінгує менеджерів (тихих обіцянок більше нема)
- Правило: якщо клієнт надіслав кілька повідомлень — ШІ опрацьовує всі (підтвердження замовлення + нове питання в одній відповіді)`,  },
  {
    id: "sales-ig-prompt-sections",
    title: "IG Manager: структурований промпт спілкування (секції)",
    area: "sales",
    description: "«📝 Промпт» розбито на вкладки: скрипт, доставка, повернення/обмін, заборони, реквізити, системні правила. Заповнена політика повернень — ШІ відповідає на питання сам",
    status: "done",
    effort: "M",
    impact: 4,
    doneIn: "2026-07-17",
    details: `Раніше промпт був одним великим текстом — правка політики повернень ризикувала зламати воронку продажу поруч.

Тепер модалка «📝 Промпт» — вкладки:
- 👋 Скрипт спілкування (тон, привітання, воронка; сумісний зі старим промптом)
- 🚚 Доставка, 🔄 Повернення/обмін, ⛔ Додаткові заборони — окремі секції, кожна зберігається незалежно
- 💳 Реквізити — та сама форма, що в «Налаштуваннях продажу», тепер в одному вікні
- 🔒 Системні правила — анти-галюцинаційні правила платформи, видно але не редагуються

Розумна логіка повернень: якщо секція «Повернення/обмін» заповнена — на ПИТАННЯ про умови ШІ відповідає сам з політики; якщо клієнт вже ХОЧЕ оформити повернення конкретної речі — завжди кличе менеджера (тригер return_request). Порожня секція — будь-яке питання про повернення йде менеджеру, як раніше.`,  },

  // ─── Customer Journey: data collection sessions (5 sessions) ────────
  {
    id: "cj-data-session-1-overview",
    title: "CJ: Сесія 1 — загальний результат бізнесу",
    area: "customer_journey",
    description: "Збір базових цифр з власника: оборот, прибуток, маржа, кількість замовлень за останні 3 міс",
    status: "partial",
    effort: "S",
    impact: 3,
    note: "Базова business-card import робить частину. Треба окрему сесію для CJ контексту",
    docRef: "CJ doc — Розділ 3 (Сесія 1)",
    details: `Сесія 1 з власником (15-20 хв):
- Загальний оборот, маржа, чистий прибуток за останні 3 місяці
- Сезонність — який місяць найкращий, найгірший
- Загальна кількість замовлень / клієнтів / повторних
- Тренди: ростемо / стабільно / просідаємо?

Виходи: baseline-цифри для CJ overview cards. Без цього CJ показує лише поточні числа без контексту "це нормально чи ні".`,  },
  {
    id: "cj-data-session-2-channels",
    title: "CJ: Сесія 2 — розподіл по каналах",
    area: "customer_journey",
    description: "Скільки приносить сайт vs IG vs офлайн. Розподіл прибутку, не лише обороту",
    status: "planned",
    effort: "S",
    impact: 3,
    docRef: "CJ doc — Розділ 3 (Сесія 2)",
    details: `Сесія 2 (10-15 хв):
- Скільки приносить кожний канал (грн): сайт / IG / TG / FB / офлайн / маркетплейси?
- Розподіл прибутку (не лише обороту) — деякі канали з низькою маржею
- Найкращий ROI канал
- Канали які власник вважає недопрацьованими

Виходи: 3-channel-cards у CJ overview наповнюються реальними цифрами. Власник бачить "де реальна головна гра".`,  },
  {
    id: "cj-data-session-3-site",
    title: "CJ: Сесія 3 — сайтова воронка",
    area: "customer_journey",
    description: "Перевірити чи всі GA4 events є + які реальні CR-числа. Звірити з очікуваннями власника",
    status: "partial",
    effort: "S",
    impact: 3,
    note: "Site funnel показує live. Треба session script для пояснення розривів",
    docRef: "CJ doc — Розділ 3 (Сесія 3)",
    details: `Сесія 3 (15-20 хв) — глибока:
- Чи є GA4? Чи всі обов'язкові події (view_item / add_to_cart / begin_checkout / purchase)?
- Реальні CR-числа на стадіях
- Очікувані власником CR vs реальні (часто різні)

Якщо власник каже "у мене 5% CR", а GA4 показує 2.1% — це момент усвідомлення. Sесія валідує що дані які CJ показує = реальність, і знімає епічні розриви.`,  },
  {
    id: "cj-data-session-4-instagram",
    title: "CJ: Сесія 4 — Instagram воронка",
    area: "customer_journey",
    description: "% відповідей, час до відповіді, скільки конвертить у замовлення, % завислих діалогів",
    status: "partial",
    effort: "S",
    impact: 3,
    note: "IG detail page показує. Треба onboarding-сесія яка це валідує з власником",
    docRef: "CJ doc — Розділ 3 (Сесія 4)",
    details: `Сесія 4 (10-15 хв):
- % відповідей на DM
- Час до першої відповіді (медіана)
- Скільки DM конвертить у замовлення (CR DM → order)
- % завислих діалогів (без відповіді > 24 год)

Виходи: IG funnel наповнюється живими числами. Власник часто шокований "ой, у мене 32% DM без відповіді — а я думав 5%".`,  },
  {
    id: "cj-data-session-5-offline",
    title: "CJ: Сесія 5 — офлайн воронка",
    area: "customer_journey",
    description: "Скільки магазинів, foot traffic, типова конверсія, NPS, повторні відвідування",
    status: "backlog",
    effort: "S",
    impact: 2,
    docRef: "CJ doc — Розділ 3 (Сесія 5)",
    details: `Сесія 5 (10-15 хв):
- Скільки магазинів?
- Foot traffic (приблизний — лічильника може не бути)
- Типова конверсія на покупку (з POS-системи)
- NPS — чи збираєш?
- Повторні відвідування — чи є loyalty program?

Якщо більшості даних немає — нормально. Сесія показує що офлайн НЕ має лишатись сліпою зоною, треба хоча б базові цифри.`,  },
  {
    id: "cj-cross-channel",
    title: "CJ: Загальна воронка бізнесу (cross-channel)",
    area: "customer_journey",
    description: "Об'єднання сайту + IG + офлайн в одну top-level воронку. Покриває overlap (один клієнт у 2 каналах)",
    status: "planned",
    effort: "L",
    impact: 4,
    docRef: "CJ doc — Розділ 4",
    details: `Top-level воронка яка об'єднує всі канали в одну:
- Перший контакт (з будь-якого каналу)
- Розгляд (переглядав товар / питав)
- Замовлення (з будь-якого каналу)
- Оплата
- Отримання
- Повторна покупка

Покриває overlap — один клієнт у двох каналах рахується раз. Базується на customers identifier graph (Binotel + IG + email). Дає унікальний CR на бізнес-рівень, не на канал.`,  },

  // ─── Analytics: 12 KPI-blocks (granular per block from spec 10.x) ─────
  {
    id: "analytics-kpi-product-stock",
    title: "Analytics: KPI-блок «Товар / Закупка»",
    area: "analytics",
    description: "ABC-наявність, mortal stock, оборотність, ліквідність, виконання плану закупки",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 10.2",
  },
  {
    id: "analytics-kpi-cod",
    title: "Analytics: KPI-блок «Незабори»",
    area: "analytics",
    description: "% викупу за каналами + витрати на зворотну логістику + врятований дохід після нагадування",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 10.4",
  },
  {
    id: "analytics-kpi-ltv",
    title: "Analytics: KPI-блок «Клієнти / LTV / Retention»",
    area: "analytics",
    description: "Перші vs повторні, retention 30/90/365 днів, LTV, top 10% клієнтів за обігом",
    status: "planned",
    effort: "M",
    impact: 3,
    docRef: "Аналітик block — Розділ 10.5",
  },
]

/** Sort items by 80/20 score (highest first), excluding done. */
export function topPicks(items: RoadmapItem[], limit: number): RoadmapItem[] {
  return [...items]
    .filter((i) => i.status !== "done")
    .sort((a, b) => eightyTwentyScore(b) - eightyTwentyScore(a))
    .slice(0, limit)
}

export function statusCounts(items: RoadmapItem[]): Record<RoadmapStatus, number> {
  const out: Record<RoadmapStatus, number> = {
    done: 0,
    partial: 0,
    planned: 0,
    backlog: 0,
  }
  for (const i of items) out[i.status]++
  return out
}

/**
 * MVP progress — completion of items marked `mvp: true`.
 * "partial" counts as 0.5 (so a card showing 50% done isn't ignored).
 */
export function mvpProgress(items: RoadmapItem[]): {
  total: number
  done: number
  partial: number
  remaining: number
  pct: number
} {
  const mvpItems = items.filter((i) => i.mvp === true)
  let done = 0
  let partial = 0
  for (const i of mvpItems) {
    if (i.status === "done") done++
    else if (i.status === "partial") partial++
  }
  const total = mvpItems.length
  const pct = total === 0 ? 0 : Math.round(((done + partial * 0.5) / total) * 100)
  return {
    total,
    done,
    partial,
    remaining: total - done - partial,
    pct,
  }
}
